<template>
  <AntSpin :spinning="spinVisible">
    <AntRow>
      <button
        class="nav-collapse-btn"
        :title="navCollapsed ? '展開側欄' : '收合側欄'"
        @click="navCollapsed = !navCollapsed"
      >
        {{ navCollapsed ? '❯' : '❮' }}
      </button>
      <AntCol v-show="!navCollapsed" :span="5" class="editor-col nav">
        <div class="ant-tree management">
          <div
            v-for="mngtItem in managementTreeData"
            :key="mngtItem.treeKey"
            :id="mngtItem.treeKey"
            :class="['management-item', 'ant-tree-node-content-wrapper', selectedSectionState.treeKey === mngtItem.treeKey && 'ant-tree-node-selected']"
            @click="onClickManagement(mngtItem)"
          >
            {{ mngtItem.treeTitle }}
          </div>
        </div>

        <AntTabs v-model:activeKey="activeTreeTab" type="card" class="flex-col">
          <AntTabPane key="master" tab="母本" class="tree-tab">
            <SectionTree
              ref="masterTreeRef"
              type="master"
              :readOnly="readOnly"
              :activeTreeTab="activeTreeTab"
              :autoSelectDraftSection="autoSelectDraftSection"
              :isDataChanged="isDataChanged"
              :onChangeSpinVisible="val => spinVisible = val"
              :onChangeCreateDocModalVisible="onChangeCreateDocModalVisible"
              :sectionsDataByDocId="sectionsDataByDocId"
              :sectionsByWallet="sectionsByWallet"
              :originSectionsByWallet="originSectionsByWallet"
              :fieldsByWallet="fieldsByWallet"
              :originFieldsByWallet="originFieldsByWallet"
              :releaseVerSectionByDocId="releaseVerSectionByDocId"
              :releaseVerFieldsByDocId="releaseVerFieldsByDocId"
              :docsDataByDocId="docsDataByDocId"
              :templateDataByTemplateId="templateDataByTemplateId"

              :draftSection="draftSection"
              :draftSectionsByWallet="draftSectionsByWallet"
              :handleWriteDraftSection="handleWriteDraftSection"
              :selectedSectionState="selectedSectionState"
              :onSelectSectionState="onSelectSectionState"
              :showPublishDraftModal="showPublishDraftModal"
              :showSaveChangesModal="showSaveChangesModal"

              @changeActiveTreeTab="val => activeTreeTab = val"
              @changeSectionsDataByDoc="handleChangeSectionsDataByDoc"
              @changeDocsDataByDocId="handleChangeDocsDataByDocId"
            />
          </AntTabPane>

          <AntTabPane key="doc" tab="子本">
            <SectionTree
              ref="docTreeRef"
              type="doc"
              :readOnly="readOnly"
              :activeTreeTab="activeTreeTab"
              :autoSelectDraftSection="autoSelectDraftSection"
              :isDataChanged="isDataChanged"
              :onChangeSpinVisible="val => spinVisible = val"
              :onChangeCreateDocModalVisible="onChangeCreateDocModalVisible"
              :sectionsDataByDocId="sectionsDataByDocId"
              :sectionsByWallet="sectionsByWallet"
              :originSectionsByWallet="originSectionsByWallet"
              :fieldsByWallet="fieldsByWallet"
              :originFieldsByWallet="originFieldsByWallet"
              :releaseVerSectionByDocId="releaseVerSectionByDocId"
              :releaseVerFieldsByDocId="releaseVerFieldsByDocId"
              :docsDataByDocId="docsDataByDocId"
              :templateDataByTemplateId="templateDataByTemplateId"

              :draftSection="draftSection"
              :draftSectionsByWallet="draftSectionsByWallet"
              :handleWriteDraftSection="handleWriteDraftSection"
              :selectedSectionState="selectedSectionState"
              :onSelectSectionState="onSelectSectionState"
              :showPublishDraftModal="showPublishDraftModal"
              :showSaveChangesModal="showSaveChangesModal"

              @changeActiveTreeTab="val => activeTreeTab = val"
              @changeSectionsDataByDoc="handleChangeSectionsDataByDoc"
              @changeDocsDataByDocId="handleChangeDocsDataByDocId"
            />
          </AntTabPane>
        </AntTabs>
      </AntCol>

      <AntCol :span="navCollapsed ? 24 : 19" class="editor-col">
        <AntForm
          ref="formRef"
          :model="selectedSectionState"
          class="section-form"
          :labelCol="{ span: 3 }"
          :wrapperCol="{ span: 21 }"
          labelAlign="left"
          :hideRequiredMark="true"
        >
          <div class="header-row">
            <div class="header-title">
              <AntTag
                v-show="!isManagement"
                style="font-size: 14px;"
                :color="selectedTreeItemState.tagColor"
              >
                {{ selectedTreeItemState.tagName }}
              </AntTag>
              <h3>
                <a
                  v-if="!isMasterSectionState && !isManagement"
                  :href="docLink"
                  target="_blank"
                  title="Open API Doc"
                >
                  {{ selectedTreeItemState.headerTitle }}
                </a>
                <span v-else>
                  {{ selectedTreeItemState.headerTitle }}
                </span>
              </h3>
              <div
                v-show="!publishButtonDisabled && !readOnly"
                class="draft-text"
              >
                Draft saved.
                <DeleteIcon
                  class="discard-draft-icon"
                  title="捨棄草稿"
                  @click="() => onDiscardDraft(true)"
                />
              </div>
              <div v-show="readOnly" class="draft-text">
                {{`${editingStatusState.username} 正在編輯中${editingStatusState.editTime ? ` (${editingStatusState.editTime})` : ''}，目前為 Read Only 模式`}}
              </div>
            </div>
            <div class="header-buttons">
              <div v-show="!isMasterSectionState && !isManagement" class="header-icon-buttons">
                <a
                  :href="docLink"
                  target="_blank"
                  style="color: rgba(0, 0, 0, 0.85);"
                >
                  <NewWindowIcon
                    title="Open API Doc"
                  />
                </a>
                <LinkIcon
                  title="Copy API Doc Link"
                  @click="handleCopyDocLink"
                />
                <DownloadIcon
                  title="Download API Doc PDF"
                  @click="handleDownload"
                />
              </div>
              <AntButton
                v-show="!isManagement && !readOnly"
                type="primary"
                :disabled="publishButtonDisabled"
                @click="() => setDraftModalType('publish')"
              >
                Publish
              </AntButton>
            </div>
          </div>

          <div class="section-container">
            <div
              class="section-container-body"
              :class="{
                'section-container-body--blocked': readOnly && !isManagement,
                'section-container-body--fill': selectedSectionState.treeKey === 'activity',
              }"
            >
            <div
              v-if="selectedSectionState.docTitle || selectedSectionState.enTitle || selectedSectionState.cnTitle"
              style="display: flex; align-items: center; margin-bottom: 24px;
            ">
              <h3 v-if="selectedSectionState.docTitle">{{ selectedSectionState.docShortTitleWithoutRemark }}</h3>
              <h3 v-else>{{ `${selectedSectionState.enTitle || ''} ${selectedSectionState.cnTitle || ''}` }}</h3>

              <h3 class="remark">
                <span class="remark-text">{{ selectedSectionState.remark || '' }}</span>
                <EditIcon
                  v-if="selectedSectionState.remark"
                  class="edit-remark-icon"
                  title="Edit Remark"
                  @click="() => {
                    remarkModalState.visible = true
                    remarkModalState.remark = selectedSectionState.remark
                  }"
                />
              </h3>

              <div
                v-if="!selectedSectionState.remark"
                class="add-remark"
                @click="() => {
                  remarkModalState.visible = true
                  remarkModalState.remark = selectedSectionState.remark
                }"
              >
                <AddIcon />
                Add Remark
              </div>
            </div>

            <div
              v-if="isDev"
              style="border: 1px solid #eee; padding: 8px 12px; margin: 16px 0;"
            >
              <div>isManagement: {{ isManagement }}</div>
              <div>selectedTreeItemState: {{ selectedTreeItemState }}</div>
              <div style="display: flex;">
                <DevDebugDataBlock title="draftSection" :data="draftSection" />
                <DevDebugDataBlock title="draftSectionsByWallet" :data="draftSectionsByWallet" />
                </div>
              <div style="display: flex;">
                <DevDebugDataBlock title="originalDocsDataByDocId" :data="originalDocsDataByDocId" />
                <DevDebugDataBlock title="docsDataByDocId" :data="docsDataByDocId" />
                </div>

              <div style="display: flex;">
                <DevDebugDataBlock
                  :title="`originSectionsByWallet[${selectedSectionState.wallet}][${selectedSectionState.sectionId}]`"
                  :data="originSectionsByWallet[selectedSectionState.wallet]?.[selectedSectionState.sectionId] || {}"
                  width="25%"
                />
                <DevDebugDataBlock
                  :title="`sectionsByWallet[${selectedSectionState.wallet}][${selectedSectionState.sectionId}]`"
                  :data="sectionsByWallet[selectedSectionState.wallet]?.[selectedSectionState.sectionId] || {}"
                  width="25%"
                />
                <DevDebugDataBlock
                  :title="`draftSection.updatedDataBySectionId[${selectedSectionState.sectionId}]`"
                  :data="draftSection?.updatedDataBySectionId?.[selectedSectionState.sectionId] || {}"
                  width="25%"
                />
                <DevDebugDataBlock
                  title="selectedSectionState"
                  :data="selectedSectionState"
                  width="25%"
                />
              </div>
              <br>
              <div style="display: flex;">
                <DevDebugDataBlock
                  :title="`fieldsByWallet[${selectedSectionState.wallet}][${selectedSectionState.sectionId}]`"
                  :data="fieldsByWallet[selectedSectionState.wallet]?.[selectedSectionState.sectionId] || {}"
                  width="50%"
                />
                <DevDebugDataBlock
                  title="selectedFieldState"
                  :data="selectedFieldState"
                  width="50%"
                />
              </div>
              <div>isDataChanged: {{ isDataChanged }}</div>
              <div>isSectionStateEqual: {{ isSectionStateEqual }}</div>
              <div>isFieldStateEqual: {{ isFieldStateEqual }}</div>
              <div>isMasterSectionState && applyDocsRef?.checkedKeys?.length === 1: {{ isMasterSectionState && applyDocsRef?.checkedKeys?.length === 1 }}</div>
              <div>isSectionStateEqual && !selectedSectionState.appliedDocs: {{ isSectionStateEqual && !selectedSectionState.appliedDocs }}</div>
            </div>

            <TemplateManagement
              v-if="selectedSectionState.treeKey === 'template'"
              :readOnly="readOnly"
              :templatesData="managementData.template || []"
              :onChangeManagementData="onChangeManagementData"
            />

            <PlatformClientManagement
              v-if="selectedSectionState.treeKey === 'platform'"
              ref="platformClientManagementRef"
              type="platform"
              :readOnly="readOnly"
              :platformsClientsData="managementData.platform"
              :onChangeManagementData="onChangeManagementData"
              @beforeDelete="onBeforeDeletePlatform"
            />

            <UserGuide
              v-if="selectedSectionState.treeKey === 'guide'"
              :readOnly="readOnly"
              :guidesData="managementData.guide || []"
              :imageManagementData="managementData.image"
              :sectionsDataByDocId="sectionsDataByDocId"
              :onChangeManagementData="onChangeManagementData"
            />

            <ActivityLogPanel
              v-if="selectedSectionState.treeKey === 'activity'"
              :docsDataByDocId="docsDataByDocId"
              :sectionsByWallet="sectionsByWallet"
              :entirePlatformsMap="entirePlatformsMap"
            />

            <template v-if="selectedSectionState.sectionType">
              <AntFormItem v-if="isDev" name="tag" label="Tag">
                <AntInput v-model:value="selectedSectionState.tag" />
              </AntFormItem>

              <AntFormItem name="title" label="English Title" v-bind="validateInfos.enTitle">
                <AntInput v-model:value="selectedSectionState.enTitle" />
                <!-- <div v-if="errors.title" class="ant-form-item-has-error">{{ errors.title }}</div> -->
              </AntFormItem>

              <AntFormItem name="title" label="Chinese Title" v-bind="validateInfos.cnTitle">
                <AntInput v-model:value="selectedSectionState.cnTitle" />
                <!-- <div v-if="errors.title" class="ant-form-item-has-error">{{ errors.title }}</div> -->
              </AntFormItem>

              <AntRow v-if="selectedSectionState.sectionType === 'api'" :gutter="16">
                <AntCol :span="12" class="tree">
                  <AntFormItem
                    name="path"
                    label="Path"
                    :labelCol="{ span: 6 }"
                    :wrapperCol="{ span: 18 }"
                    v-bind="validateInfos.path"
                  >
                    <AntInput v-model:value="selectedSectionState.path" />
                  </AntFormItem>
                </AntCol>

                <AntCol :span="12" class="tree">
                  <AntFormItem
                    name="operationId"
                    :labelCol="{ span: 4 }"
                    :wrapperCol="{ span: 20 }"
                    v-bind="validateInfos.operationId"
                  >
                    <template #label>
                      <div style="display: flex; align-items: center; gap: 4px;">
                        Operation ID
                        <AntTooltip
                          :title="`用於設置此 API 區塊的 URL，如：https://allwecan.info/api-doc/multi-wallet/PRD#operation/${selectedSectionState.operationId || 'doLoginAndLaunchGame'}，系統會由 Path 自動生成 Operation ID`"
                          placement="rightTop"
                        >
                          <QuestionIcon style="color: #1890ff;" />
                        </AntTooltip>
                      </div>
                    </template>
                    <AntInput v-model:value="selectedSectionState.operationId" disabled />
                  </AntFormItem>
                </AntCol>
              </AntRow>

              <AntFormItem v-if="!isReleaseVer" name="platforms" label="Platforms">
                <PlatformDocSelect
                  type="platforms"
                  :value="selectedSectionState.platforms"
                  :options="platformOptions"
                  :onChange="val => onChangeSelectedSectionState('platforms', val)"
                />
              </AntFormItem>

              <AntFormItem v-if="!isReleaseVer && !selectedSectionState.platforms" name="excludedPlatforms" label="Excluded Platforms">
                <PlatformDocSelect
                  type="excludedPlatforms"
                  :value="selectedSectionState.excludedPlatforms"
                  :options="entirePlatformOptions"
                  :onChange="val => onChangeSelectedSectionState('excludedPlatforms', val)"
                />
              </AntFormItem>

              <AntFormItem v-if="!isReleaseVer" name="docs" label="Used In">
                <div v-if="isDev">shouldBeRemovedDocs: {{ shouldBeRemovedDocs }}</div>
                <AntSelect
                  mode="multiple"
                  optionFilterProp="label"
                  showSearch
                  style="width: 100%"
                  :tagRender="renderDocSelectTag"
                  :value="selectedSectionState.docs"
                  :options="sortDocIds(Object.keys(docsDataByDocId))
                    .filter(docId => selectedSectionState.wallet === docsDataByDocId[docId].wallet)
                    .map(docId => ({ value: docId, label: docsDataByDocId[docId].docShortTitle }))"
                  @change="val => onChangeSelectedSectionState('docs', sortDocIds(val))"
                />
              </AntFormItem>

              <ReleaseVerSection
                v-if="isReleaseVer"
                ref="releaseVerSectionRef"
                :imageManagementData="managementData.image"
                :selectedSectionState="selectedSectionState"
                :selectedFieldState="selectedFieldState"
                :releaseVerFieldsByDocId="releaseVerFieldsByDocId"
                :sectionsDataByDocId="sectionsDataByDocId"
                :docsDataByDocId="docsDataByDocId"
              />

              <ApiSection
                v-else-if="selectedSectionState.sectionType === 'api'"
                ref="apiSectionRef"
                :imageManagementData="managementData.image"
                :docsDataByDocId="docsDataByDocId"
                :sectionsDataByDocId="sectionsDataByDocId"
                :selectedSectionState="selectedSectionState"
                :selectedFieldState="selectedFieldState"
                :platformOptions="platformOptions"
                @change="onChangeSelectedSectionState"
              />

              <AppendixSection
                v-else-if="selectedSectionState.sectionType === 'appendix'"
                ref="appendixSectionRef"
                :imageManagementData="managementData.image"
                :docsDataByDocId="docsDataByDocId"
                :selectedSectionState="selectedSectionState"
                :selectedFieldState="selectedFieldState"
                :sectionsBySectionId="sectionsBySectionId"
                :sectionsDataByDocId="sectionsDataByDocId"
                :platformOptions="platformOptions"
                @change="onChangeSelectedSectionState"
              />

              <div v-else>

                <div v-if="isDev">customizedVersionSelectedDocId: {{ customizedVersionSelectedDocId }}</div>
                <AntInputGroup compact style="margin: 48px 0 12px;">
                  <AntInput
                    class="prefix-input ant-col-3"
                    placeholder="Switch Customized Version"
                    defaultValue="Switch Customized Version"
                    readonly
                  />
                  <CustomizedVersionSelect
                    class="ant-col-21"
                    :selectedDocId="customizedVersionSelectedDocId"
                    :docsDataByDocId="docsDataByDocId"
                    :selectedSectionState="selectedSectionState"
                    :selectedSectionOrFieldState="selectedSectionState"
                    @update:selectedDocId="val => customizedVersionSelectedDocId = val === 'general' ? undefined : val"
                  />

                </AntInputGroup>
                <SectionEditor
                  height="700px"
                  :previewMode="false"
                  :customizedVersionSelectedDocId="customizedVersionSelectedDocId || `${selectedSectionState.wallet}#master`"
                  :sectionsDataByDocId="sectionsDataByDocId"
                  :imageManagementData="managementData.image"
                  :editorContent="selectedSectionState.customizedVersion?.[customizedVersionSelectedDocId]?.content || selectedSectionState.content"
                  :onChange="val => {
                    if (customizedVersionSelectedDocId) {
                      selectedSectionState.customizedVersion[customizedVersionSelectedDocId].content = val
                    } else {
                      selectedSectionState.content = val
                    }
                  }"
                />
              </div>
            </template>

            <EditDoc
              v-if="selectedSectionState.docTitle"
              ref="editDocRef"
              :validateInfos="validateInfos"
              :docSectionsData="sectionsDataByDocId[selectedSectionState.docId] || []"
              :masterSectionsData="sectionsDataByDocId[`${selectedSectionState.wallet}#master`]"
              :selectedSectionState="selectedSectionState"
              :fieldsBySectionId="fieldsBySectionId"
              :sectionsBySectionId="sectionsBySectionId"
              :templateOptions="templateOptions"
              :platformOptions="platformOptions"
              :entirePlatformOptions="entirePlatformOptions"
              :updateAllowedIPs="handleUpdateAllowedIPs"
              @change="onChangeSelectedSectionState"
              @addDocToSection="onAddDocToSectionFromEditDoc"
              @removeDocFromSection="onRemoveDocFromSectionFromEditDoc"
            />
            </div>

            <div
              v-if="readOnly && !isManagement"
              class="editing-status-overlay"
            />
          </div>

          <div v-show="isDev || !isManagement" class="footer-row">
            <AntButton
              v-show="!isManagement && isDataChanged"
              id="cancel"
              @click="onCancel"
            >
              Cancel
            </AntButton>
            <AntButton
              v-show="!isManagement && (selectedSectionState.docTitle ? isDataChanged : true)"
              type="primary"
              @click="onSave"
            >
              {{ isDataChanged ? `Save${selectedSectionState.docTitle ? '' : ' & Preview'}` : 'Preview' }}
            </AntButton>
          </div>
        </AntForm>
      </AntCol>
    </AntRow>

    <AntModal
      :visible="draftModalVisible"
      width="90%"
      :bodyStyle="{
        height: '80vh',
        overflow: 'hidden',
        padding: 0,
      }"
      centered
      v-bind="draftModalProps"
      @afterClose="() => { draftModalVisible = false }"
    >
      <template #title>
        <span>
          {{
            stagedSection.sectionId?.startsWith('staged-delete-')
            ? `Delete Platform: ${entirePlatformsMap[stagedSection.deleteInfo.id]}`
            : `${titleCase(draftSection.wallet)} Wallet`
          }}
        </span>
        <span style="color: dodgerblue; margin-left: 8px;">{{ draftModalType === 'publish' ? '發佈版本' : '草稿' }}</span>
      </template>
      <DraftChange
        ref="draftChangeRef"
        :visible="draftModalVisible"
        :type="draftModalType"
        :stagedSection="stagedSection"
        :draftSection="draftSection"
        :originSectionsByWallet="originSectionsByWallet"
        :sectionsByWallet="sectionsByWallet"
        :originFieldsByWallet="originFieldsByWallet"
        :sectionsDataByDocId="sectionsDataByDocId"
        :releaseVerFieldsByDocId="releaseVerFieldsByDocId"
        :docsDataByDocId="docsDataByDocId"
        :originalDocsDataByDocId="originalDocsDataByDocId"
        :platformOptions="platformOptions"
        :entirePlatformsMap="entirePlatformsMap"
        :imageManagementData="managementData.image"
        :onDiscardDraft="onDiscardDraft"
      />
      <template #footer>
        <div :style="{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }">
          <div :style="{ visibility: draftModalType === 'publish' ? 'visible' : 'hidden' }">
            ※確認完所有 <AntTag
              style="margin: 0;"
              color="blue"
            >
              子本
            </AntTag> 的「更動細節」、「更版說明」後，才可提交發佈。
          </div>
          <div style="display: flex;">
            <AntButton @click="draftModalProps.onCancel?.()">
              {{ draftModalProps.cancelText }}
            </AntButton>
            <AntButton
              type="primary"
              :disabled="draftModalProps.okButtonProps?.disabled"
              @click="draftModalProps.onOk?.()"
            >
              {{ draftModalProps.okText }}
            </AntButton>
          </div>
        </div>
      </template>
    </AntModal>

    <AntModal
      width="90%"
      :bodyStyle="{ height: '80vh', overflowY: 'auto', padding: 0 }"
      :visible="!!applyDocsModalVisible"
      title="Preview"
      okText="Save"
      :okButtonProps="{ disabled: !isDataChanged }"
      cancelText="Cancel"
      centered
      @ok="onApplyDocsModalOk"
      @cancel="applyDocsModalVisible = false"
    >
      <ApplyDocs
        ref="applyDocsRef"
        :visible="applyDocsModalVisible"
        :selectedSectionState="selectedSectionState"
        :selectedFieldState="selectedFieldState"
        :sectionsBySectionId="sectionsBySectionId"
        :sectionsDataByDocId="sectionsDataByDocId"
        :docsDataByDocId="docsDataByDocId"
        :templateDataByTemplateId="templateDataByTemplateId"
        :entirePlatformOptions="entirePlatformOptions"
      />
    </AntModal>

    <AntModal
      :visible="remarkModalState.visible"
      title="Remark"
      okText="Save"
      cancelText="Cancel"
      @ok="handleUpdateRemark"
      @cancel="() => {
        remarkModalState.visible = false
        remarkModalState.remark = undefined
      }"
    >
      <AntInput
        v-model:value="remarkModalState.remark"
        placeholder="Enter remark"
      />
    </AntModal>

    <AntModal
      :visible="editingStatusState.modalVisible && !editingStatusState.isEditing"
      :okButtonProps="{ disabled: !editingStatusState.username }"
      :cancelButtonProps="{ style: { display: 'none' } }"
      :zIndex="1001"
      :closable="false"
      :maskClosable="false"
      @ok="handleSetEditorUsernameToLocalStorage"
    >
        <p>Please enter your name:</p>
        <AntInput
          v-model:value="editingStatusState.username"
          placeholder="Name"
        />
    </AntModal>

    <CreateDocModal
      :visible="createDocModalVisible"
      :docsDataByDocId="docsDataByDocId"
      :sectionsByWallet="sectionsByWallet"
      :fieldsByWallet="fieldsByWallet"
      :templateDataByTemplateId="templateDataByTemplateId"
      :templateOptions="templateOptions"
      :platformOptions="entirePlatformOptions"
      :handleCreateDoc="docTreeRef?.handleCreateDoc"
      :onChangeCreateDocModalVisible="onChangeCreateDocModalVisible"
    />

    <SortOrderUpdateModal />
  </AntSpin>
</template>

<script>
/* eslint-disable no-unused-vars */
import {
  h,
  ref,
  reactive,
  inject,
  watch,
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  nextTick,
  watchEffect,
} from 'vue'

import { useRouter } from 'vue-router'
import { cloneDeep, isEqual, isEmpty } from 'lodash'
import moment from 'moment'
import ShortUniqueId from 'short-unique-id'
import axios from 'axios'

import {
  Spin as AntSpin,
  Row as AntRow,
  Col as AntCol,
  Form as AntForm,
  Tabs as AntTabs,
  Modal as AntModal,
  Button as AntButton,
  Input as AntInput,
  Select as AntSelect,
  Tooltip as AntTooltip,
  message as antMessage,
  Tag as AntTag,
  Popover as AntPopover,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import EditIcon from '@ant-design/icons-vue/lib/icons/EditOutlined'
import NewWindowIcon from '@ant-design/icons-vue/lib/icons/ExportOutlined'
import LinkIcon from '@ant-design/icons-vue/lib/icons/LinkOutlined'
import DownloadIcon from '@ant-design/icons-vue/lib/icons/DownloadOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'
import QuestionIcon from '@ant-design/icons-vue/lib/icons/QuestionCircleOutlined'

import PlatformDocSelect from '@/components/ApiDoc/shared/PlatformDocSelect.vue'
import SectionTree from '@/components/ApiDoc/editor/SectionTree.vue'
import SectionEditor from '@/components/ApiDoc/editor/SectionEditor.vue'
import ReleaseVerSection from '@/components/ApiDoc/editor/ReleaseVerSection.vue'
import ApiSection from '@/components/ApiDoc/editor/ApiSection.vue'
import AppendixSection from '@/components/ApiDoc/editor/AppendixSection.vue'
import EditDoc from '@/components/ApiDoc/doc/EditDoc.vue'
import TemplateManagement from '@/components/ApiDoc/management/TemplateManagement.vue'
import PlatformClientManagement from '@/components/ApiDoc/management/PlatformClientManagement.vue'
import UserGuide from '@/components/ApiDoc/management/UserGuide.vue'
import ActivityLogPanel from '@/components/ApiDoc/management/ActivityLog/ActivityLogPanel.vue'
import DraftChange from '@/components/ApiDoc/publish/DraftChange.vue'
import CreateDocModal from '@/components/ApiDoc/doc/CreateDocModal.vue'
import ApplyDocs from '@/components/ApiDoc/publish/ApplyDocs.vue'
import CustomizedVersionSelect from '@/components/ApiDoc/editor/CustomizedVersionSelect.vue'
import DevDebugDataBlock from '@/components/ApiDoc/shared/DevDebugDataBlock.vue'
import SortOrderUpdateModal from '@/components/ApiDoc/SortOrderUpdateModal.vue'

import {
  shouldDisplay,
  cleanSection,
  getFieldPublishFlagsForDocPlatformChange,
  sortDocIds,
  sortSectionsObj,
  getDocShortTitle,
  deriveDocDataWithTitlesAndTemplateData,
  resyncDocCustomizedSortOrderByMasterOrder,
  upsertDraftSectionEntries,
  normalizeUpdatedDocByDocId,
  hasUpdatedDoc,
  getUpdatedDocIds,
  setUpdatedDocEntry,
  applyUpdatedDocMapToDocsData,
  getApiDocLink,
} from '@/components/ApiDoc/lib/libsV3'

import { emptyObject, titleCase } from '@/utils'
import { isRootParentSectionId } from '@/components/ApiDoc/lib/constants'
import { ACTIVITY_ACTIONS } from '@/components/ApiDoc/management/ActivityLog/constants'

import {
  getDocs,
  updateDoc,
  getSections,
  updateSection,
  batchWriteSections,
  getManagment,
  getManagementByEntityTypeAndId,
  updateManagementItem,
  createManagementItem,
  getFields,
  batchWriteFields,
  batchDeleteFields,
  createActivityLog,
} from '@/api/apiDocV3'

import {
  buildPublishActivityDetail,
  enrichPublishedDocsWithUpdatedDocFieldDiffs,
  buildReleaseVerRowDiffs,
  buildReleaseVerPublishSnapshotEntry,
  mergeReleaseVerPublishSnapshotEntry,
  resolvePublishLogTarget,
  isMasterPublishedDocId,
} from '@/components/ApiDoc/publish/publishDiff'

const { useForm } = AntForm

export default {
  name: 'ApiDocSectionEditor',
  components: {
    SectionTree,
    ReleaseVerSection,
    ApiSection,
    AppendixSection,
    EditDoc,
    TemplateManagement,
    PlatformClientManagement,
    UserGuide,
    ActivityLogPanel,
    SectionEditor,
    PlatformDocSelect,
    DraftChange,
    ApplyDocs,
    CreateDocModal,
    SortOrderUpdateModal,
    CustomizedVersionSelect,
    DevDebugDataBlock,
    AntSpin,
    AntRow,
    AntCol,
    AntTabs,
    AntTabPane: AntTabs.TabPane,
    AntForm,
    AntFormItem: AntForm.Item,
    AntButton,
    AntInput,
    AntInputGroup: AntInput.Group,
    AntSelect,
    AntModal,
    AntTag,
    AntTooltip,
    AddIcon,
    EditIcon,
    NewWindowIcon,
    LinkIcon,
    DownloadIcon,
    DeleteIcon,
    QuestionIcon,
  },
  setup() {
    const nodeEnv = inject('nodeEnv')
    const isDev = inject('isDev')
    const router = useRouter()

    const managementData = reactive({})

    const docsDataByDocId = reactive({})
    const originalDocsDataByDocId = ref({})

    const sectionsData = reactive([])

    const managementTreeData = [
      {
        treeTitle: 'Template Management',
        treeKey: 'template',
        treePos: 0,
        docId: 'template',
      },
      {
        treeTitle: 'Platform Management',
        treeKey: 'platform',
        treePos: 1,
        docId: 'platform',
      },
      {
        treeTitle: 'User Guide',
        treeKey: 'guide',
        treePos: 2,
        docId: 'guide',
      },
      {
        treeTitle: 'Activity Log',
        treeKey: 'activity',
        treePos: 3,
        docId: 'activity',
      },
    ]

    const selectedSectionState = reactive({ ...managementTreeData[0] })
    console.log('selectedSectionState', selectedSectionState)
    const originSectionsByWallet = reactive({ single: {}, multi: {} }) // 沒有套用草稿變更
    const sectionsByWallet = reactive({ single: {}, multi: {} }) // 有套用草稿變更
    const sectionsBySectionId = computed(() => sectionsByWallet[selectedSectionState.wallet || 'multi'])
    const releaseVerSectionByDocId = reactive({})
    const releaseVerFieldsByDocId = reactive({})

    const sectionsDataByDocId = computed(() => {
      const result = {
        'multi#master': {},
        'single#master': {},
      } // 為了排序在最前，先定義

      Object.values(sectionsByWallet)
        .flatMap(walletData => Object.values(walletData))
        .forEach(({ customizedVersion = {}, docs, ...section }) => {
          const { sectionId } = section
          if (section.docId.includes('master')) {
            result[section.docId][sectionId] = { customizedVersion, docs, ...section }

            if (!docs) console.warn('docs is not defined', section);
            (docs || []).forEach((docId) => {
              if (!result[docId]) result[docId] = {}

              result[docId][sectionId] = {
                ...section,
                ...customizedVersion[docId],
                docId,
              }
            })
          } else {
            if (!result[section.docId]) result[section.docId] = {}
            result[section.docId][sectionId] = section
          }
        })

      // 新建子本尚未被任何母本 section.docs 引用時，仍保留空陣列供子本樹／EditDoc 使用
      Object.keys(docsDataByDocId).forEach((docId) => {
        if (docId.includes('master')) return
        if (!result[docId]) result[docId] = {}
      })

      Object.keys(result).forEach((docId) => {
        result[docId] = sortSectionsObj(result[docId])
      })

      console.log('[ApiDocSectionEditor] computed sectionsDataByDocId', result)
      return result
    })

    const selectedFieldState = reactive({}) // { request: {}, response: {}, row: {} }
    const originFieldsByWallet = reactive({ single: {}, multi: {} }) // 沒有套用草稿變更
    const fieldsByWallet = reactive({ single: {}, multi: {} }) // 有套用草稿變更
    const fieldsBySectionId = computed(() => fieldsByWallet[selectedSectionState.wallet])
    const entirePlatformOptions = computed(() => (managementData.platform || []).map(({ id, platformName }) => ({ value: id, label: platformName })))
    const entirePlatformsMap = computed(() => Object.fromEntries([['all', 'ALL'], ...entirePlatformOptions.value.map(({ value, label }) => [value, label])]))

    const shouldBeRemovedDocs = computed(() => (selectedSectionState.docs || []).filter(docId => !shouldDisplay({ ...selectedSectionState, docs: null }, docsDataByDocId[docId])))
    const popoverOpenByDocId = reactive({})

    watch(shouldBeRemovedDocs, (nextDocIds) => {
      nextDocIds.forEach((docId) => {
        if (popoverOpenByDocId[docId] === undefined) popoverOpenByDocId[docId] = true
      })
      Object.keys(popoverOpenByDocId).forEach((docId) => {
        if (!nextDocIds.includes(docId)) delete popoverOpenByDocId[docId]
      })
    }, { immediate: true })
    const renderDocSelectTag = ({
      label, value, closable, onClose,
    }) => {
      const isShouldBeRemovedDoc = shouldBeRemovedDocs.value.includes(value)
      const tagNode = h('span', {
        class: 'ant-select-selection-overflow-item',
        onMouseenter: () => {
          if (isShouldBeRemovedDoc) popoverOpenByDocId[value] = true
        },
      }, [
        h(
          'span',
          {
            class: 'ant-select-selection-item',
            style: isShouldBeRemovedDoc
              ? { background: '#fff7e6', color: '#fa8c16', borderColor: '#ffd591' }
              : {},
          },
          [
            h('span', { class: 'ant-select-selection-item-content' }, label),
            ...(closable
              ? [h(
                'span',
                {
                  class: 'ant-select-selection-item-remove',
                  onMousedown: (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  },
                  onClick: onClose,
                },
                [
                  h(
                    'span',
                    {
                      role: 'img',
                      'aria-label': 'close',
                      class: 'anticon anticon-close',
                    },
                    [
                      h(
                        'svg',
                        {
                          focusable: 'false',
                          class: '',
                          'data-icon': 'close',
                          width: '1em',
                          height: '1em',
                          fill: 'currentColor',
                          'aria-hidden': 'true',
                          'fill-rule': 'evenodd',
                          viewBox: '64 64 896 896',
                        },
                        [
                          h('path', {
                            d: 'M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z',
                          }),
                        ],
                      ),
                    ],
                  ),
                ],
              )]
              : []),
          ],
        ),
      ])
      if (!isShouldBeRemovedDoc) return tagNode

      return h(
        AntPopover,
        {
          open: popoverOpenByDocId[value] !== false,
          visible: popoverOpenByDocId[value] !== false,
          defaultVisible: popoverOpenByDocId[value] !== false,
          trigger: 'hover',
          onOpenChange: (nextOpen) => {
            if (nextOpen) popoverOpenByDocId[value] = true
          },
          onVisibleChange: (nextOpen) => {
            if (nextOpen) popoverOpenByDocId[value] = true
          },
          overlayStyle: {
            maxWidth: '210px',
          },
          overlayClassName: 'doc-warning-popover',
          placement: 'bottom',
        },
        {
          content: () => h(
            'div',
            {
              style: {
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: '8px',
              },
            },
            [
              h(
                'div',
                {
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  },
                },
                [
                  h('span', '此子本設定之 Platforms / Excluded Platforms 與上方的區塊 Platforms / Excluded Platforms 不相符，建議移除'),
                  h(
                    'span',
                    {
                      role: 'button',
                      tabindex: 0,
                      style: {
                        cursor: 'pointer',
                        color: '#999',
                        fontSize: '12px',
                        lineHeight: 1,
                      },
                      onClick: (e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        popoverOpenByDocId[value] = false
                      },
                    },
                    '✕',
                  ),
                ],
              ),
              h(
                'div',
                [
                  h('div', { style: { marginBottom: '4px', fontWeight: 'bold' } }, '子本 Platforms:'),
                  h(
                    'div',
                    { class: 'ant-select-multiple' },
                    (docsDataByDocId[value]?.platforms || []).length
                      ? docsDataByDocId[value].platforms.map(pid => h(
                        AntTag,
                        { style: { marginBottom: '4px' } },
                        () => entirePlatformsMap.value[pid] || pid,
                      ))
                      : [h(AntTag, { style: { marginBottom: '4px' } }, () => 'ALL')],
                  ),
                ],
              ),
              ...(!(docsDataByDocId[value]?.platforms || []).length
                ? [h(
                  'div',
                  [
                    h('div', { style: { marginBottom: '4px', fontWeight: 'bold' } }, '子本 Excluded Platforms:'),
                    h(
                      'div',
                      { class: 'ant-select-multiple' },
                      (docsDataByDocId[value]?.excludedPlatforms || []).length
                        ? docsDataByDocId[value].excludedPlatforms.map(pid => h(
                          AntTag,
                          { style: { marginBottom: '4px' } },
                          () => entirePlatformsMap.value[pid] || pid,
                        ))
                        : [h('span', { style: { marginBottom: '4px' } }, () => '-')],

                    ),
                  ],
                )]
                : []),
            ],
          ),
          default: () => tagNode,
        },
      )
    }

    const activeTreeTab = ref('master')
    const navCollapsed = ref(false)
    const autoSelectDraftSection = ref(false)

    const masterTreeRef = ref(null)
    const docTreeRef = ref(null)
    const editDocRef = ref(null)
    const apiSectionRef = ref(null)
    const appendixSectionRef = ref(null)
    const releaseVerSectionRef = ref(null)
    const draftChangeRef = ref(null)
    const platformClientManagementRef = ref(null)
    const applyDocsRef = ref(null)

    const isMasterSectionState = computed(() => (selectedSectionState.docId || '').includes('master'))
    const isManagement = computed(() => !selectedSectionState.wallet)
    const isReleaseVer = computed(() => selectedSectionState.sectionId === 'ReleaseVer')

    const docLink = computed(() => getApiDocLink({
      docId: selectedSectionState.docId,
      wallet: selectedSectionState.wallet,
      docsDataByDocId,
      isDev: isDev.value,
    }))

    const templateOptions = ref([])
    const templateDataByTemplateId = ref({})

    const isSectionStateEqual = computed(() => {
      if (isManagement.value) return true
      const { sectionId } = selectedSectionState

      let sectionData = {}
      if (isReleaseVer.value) sectionData = releaseVerSectionByDocId[selectedSectionState.docId] || {}
      else if (selectedSectionState.docTitle) {
        sectionData = docsDataByDocId[selectedSectionState.docId] || {}
      } else sectionData = sectionsBySectionId.value[sectionId] || {}

      const isStateEqual = isEqual(cleanSection(selectedSectionState), sectionData)
      console.log('isEqualSectionState', isStateEqual, { sectionData, selectedSectionState: cleanSection(selectedSectionState) })
      return isStateEqual
    })

    const isFieldStateEqual = computed(() => {
      if (isManagement.value || selectedSectionState.docTitle) return true
      const { sectionId } = selectedSectionState
      const fieldData = isReleaseVer.value ? releaseVerFieldsByDocId[selectedSectionState.docId] : fieldsBySectionId.value[sectionId]
      // if (!isReleaseVer.value && !fieldData) return true
      const isStateEqual = isEqual(selectedFieldState, fieldData || {}) || (!fieldData && Object.keys(selectedFieldState).every(key => isEmpty(selectedFieldState[key])))
      console.log('isFieldStateEqual', isStateEqual, { selectedFieldState, fieldData })
      return isStateEqual
    })

    const isDataChanged = computed(() => !isSectionStateEqual.value || !isFieldStateEqual.value)

    const selectedTreeItemState = computed(() => {
      const nodes = (isMasterSectionState.value || isManagement.value ? masterTreeRef.value?.treeNodes : docTreeRef.value?.treeNodes) || []
      const headerTitle = isMasterSectionState.value ? nodes[0] : docsDataByDocId[selectedSectionState.docId]?.docTitle
      return {
        nodes,
        headerTitle: isManagement.value ? selectedSectionState.treeTitle : headerTitle,
        tagName: isMasterSectionState.value || isManagement.value ? '母本' : '子本',
        tagColor: isMasterSectionState.value || isManagement.value ? 'orange' : 'blue',
      }
    })

    const loadAllFieldsData = (fields) => {
      fields.forEach((field) => {
        const { docId: fieldDocId, fieldId, scope } = field

        const currentSectionId = field.sectionId
        if (currentSectionId === 'ReleaseVer') {
          if (!releaseVerFieldsByDocId[fieldDocId]) releaseVerFieldsByDocId[fieldDocId] = {}
          if (!releaseVerFieldsByDocId[fieldDocId][scope]) releaseVerFieldsByDocId[fieldDocId][scope] = {}
          if (!releaseVerFieldsByDocId[fieldDocId][scope][fieldId]) releaseVerFieldsByDocId[fieldDocId][scope][fieldId] = {}
          releaseVerFieldsByDocId[fieldDocId][scope][fieldId] = field
          return
        }

        const currentWallet = field.wallet

        if (!originFieldsByWallet[currentWallet][currentSectionId]) originFieldsByWallet[currentWallet][currentSectionId] = {}
        if (!originFieldsByWallet[currentWallet][currentSectionId][scope]) originFieldsByWallet[currentWallet][currentSectionId][scope] = {}
        // if (!originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId]) originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId] = {}
        originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId] = field

        // if (!originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId].docId?.includes('master') && fieldDocId.includes('master')) {
        //   // if (fieldId === 'xVZHfWaZhs') console.log(222, originFieldsByWallet[wallet][sectionId][scope][fieldId])
        //   // acc 已存子本資料或 acc 尚無資料，遍歷到母本，將 acc 子本資料放進 customizedVersion
        //   originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId] = {
        //     ...field,
        //     customizedVersion: originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId]?.customizedVersion,
        //     docs: originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId]?.docs,
        //   }
        // } else if (!fieldDocId.includes('master')) {
        //   // if (fieldId === 'xVZHfWaZhs') console.log(333, originFieldsByWallet[wallet][sectionId][scope][fieldId])
        //   if (!originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId].docId) originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId] = field// 8.8. NTCY 孤兒 LETSGO rowId = gMu7bOqYgJ
        //   if (!originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId].customizedVersion) originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId].customizedVersion = {}
        //   const { docs, customizedVersion, ...plainField } = field
        //   originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId].customizedVersion[fieldDocId] = plainField

        //   if (!originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId].docs) originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId].docs = []
        //   originFieldsByWallet[currentWallet][currentSectionId][scope][fieldId].docs.push(fieldDocId)
        // }
      })

      // eslint-disable-next-line no-unused-vars
      const sanitizeField = (field) => {
        if (field.customizedVersion) {
          let fieldKeys = ['fieldName', 'fieldType', 'content', 'maxLength', 'numberType', 'decimalPlace', 'format', 'required', 'platforms', 'examples']
          if (field.scope === 'row') fieldKeys = Object.keys(originSectionsByWallet[field.wallet][field.sectionId].tableColumns)

          Object.keys(field.customizedVersion).forEach((doc) => {
            let isSame = true
            const customizedVersion = { docId: doc }
            fieldKeys.forEach((key) => {
              if (typeof field.customizedVersion[doc][key] === 'object' && typeof field[key] === 'object') {
                // if (key === 'examples') {
                //   if (!isEqual(field.customizedVersion[doc][key], originSectionsByWallet[field.wallet][field.sectionId][key])) {
                //     if (field.fieldId === '1bf6RMVl4C') console.log(doc, key, 'field.customizedVersion', field.customizedVersion[doc][key], 'originSectionsByWallet', originSectionsByWallet[field.wallet][field.sectionId][key])
                //     customizedVersion[key] = field.customizedVersion[doc][key]
                //     isSame = false
                //   }
                // } else
                if (!isEqual(field.customizedVersion[doc][key], field[key])) {
                  if (field.fieldId === '1bf6RMVl4C') console.log(doc, key, 'field.customizedVersion', field.customizedVersion[doc][key], 'field', field[key])
                  customizedVersion[key] = field.customizedVersion[doc][key]
                  isSame = false
                }
              } else if (field.customizedVersion[doc][key] !== field[key]) {
                customizedVersion[key] = field.customizedVersion[doc][key]
                isSame = false
              }
            })

            if (isSame) {
              delete field.customizedVersion[doc]
            } else {
              field.customizedVersion[doc] = customizedVersion
            }

            // let isSameExamples = true
            // if (field.scope !== 'row') {
            //   isSameExamples = Object.keys(originSectionsByWallet[field.wallet][field.sectionId].examples || {}).every((exampleId) => {
            //     if (field.customizedVersion[doc].examples?.[exampleId] === field.examples?.[exampleId]) {
            //       return true
            //     }
            //     return false
            //   })
            // }
            if (field.fieldId === 'xVZHfWaZhs') console.log(doc, 'isSame', isSame)
          })
        }

        // if (field.docs?.length) field.docs = sortDocIds(field.docs)

        if (field.docs?.length === originSectionsByWallet[field.wallet][field.sectionId].docs?.length) {
          delete field.docs
        } else {
          field.docs = sortDocIds(field.docs)
        }
      }

      fieldsByWallet.multi = cloneDeep(originFieldsByWallet.multi)
      fieldsByWallet.single = cloneDeep(originFieldsByWallet.single)
    }

    const applyDocsModalVisible = ref(false)
    const remarkModalState = reactive({
      visible: false,
      remark: selectedSectionState.remark,
    })

    const fetchManagment = async () => {
      const { data, error } = await getManagment()
      if (data) {
        console.log('fetchManagment data', data)
        Object.assign(managementData, data)
      } else {
        console.error(error)
      }
    }

    ['template', 'platform'].forEach((key) => {
      watch(() => managementData[key], (newVal) => {
        const options = newVal.map(d => ({ value: d.id, label: d[`${key}Name`] }))

        if (key === 'template') {
          templateOptions.value = options
          templateDataByTemplateId.value = newVal.reduce((acc, d) => { acc[d.id] = d; return acc }, {})
        }
      }, { deep: true })
    })

    const platformOptions = ref([])

    watchEffect(() => {
      if (!managementData.platform) return

      if (!isMasterSectionState.value && !isManagement.value && !selectedSectionState.docTitle) { // 子本、非 EditDoc 內
        const { platforms: docPlatforms = [], excludedPlatforms: docExcludedPlatforms = [] } = docsDataByDocId[selectedSectionState.docId] || {}
        if (docPlatforms?.length) platformOptions.value = entirePlatformOptions.value.filter(({ value }) => docPlatforms.includes(value))
        if (!docPlatforms?.length && docExcludedPlatforms?.length) platformOptions.value = entirePlatformOptions.value.filter(({ value }) => !docExcludedPlatforms.includes(value))
      } else {
        platformOptions.value = entirePlatformOptions.value
      }

      console.log('[ApiDocSectionEditor] watchEffect to update platformOptions', {
        platformOptions: platformOptions.value,
      })
    })

    const customizedVersionSelectedDocId = ref(undefined)

    watch([() => selectedSectionState.sectionId, () => selectedSectionState.wallet], () => {
      customizedVersionSelectedDocId.value = undefined
    })

    const spinVisible = ref(true)

    const getBatchWriteSectionStateByPath = (key, section) => {
      if (!key.includes('.')) return section
      const keys = key.split('.')
      let current = section
      for (let i = 0; i < keys.length - 1; i += 1) {
        if (!current[keys[i]]) current[keys[i]] = {}
        current = current[keys[i]]
      }
      return current[keys[keys.length - 1]]
    }

    onBeforeMount(async () => {
      await fetchManagment()
      const { data: docData } = await getDocs()
      console.log('docData', docData)
      Object.assign(docsDataByDocId, docData.reduce((acc, d) => {
        deriveDocDataWithTitlesAndTemplateData(d, templateDataByTemplateId.value)
        acc[d.docId] = d
        return acc
      }, {}))
      originalDocsDataByDocId.value = cloneDeep(docsDataByDocId)

      const { data: fields } = await getFields()
      const { data: sectionData } = await getSections()

      loadAllFieldsData(fields || [])
      Object.assign(sectionsData, sectionData)

      spinVisible.value = false
    })

    const editingStatusState = reactive({
      modalVisible: false,
      isEditing: false,
      username: '',
      editTime: null,
    })

    const readOnly = computed(() => editingStatusState.modalVisible && editingStatusState.isEditing)

    const draftModalType = ref(false)
    const draftModalVisible = ref(false)

    const WALLET_KEYS = ['multi', 'single']

    const getDocWallet = docId => (docId?.includes('master') ? docId?.split('#')[0] : docId?.split('#')[1])

    const getEditorRouteWallet = () => {
      const { wallet } = router.currentRoute.value.params
      return wallet === 'single' || wallet === 'multi' ? wallet : null
    }

    /** 僅在 /api-doc/editor 根路徑時，套用草稿後才自動導向草稿 section */
    const isEditorV3BaseRoute = () => router.currentRoute.value.path === '/api-doc/editor'

    const draftWalletHasContent = draft => (
      draft && (!isEmpty(draft.updatedDataBySectionId) || hasUpdatedDoc(draft))
    )

    /** 草稿彈窗目前顯示的 wallet（例如進入 /single/... 仍開 Multi 草稿時設為 multi） */
    const activeDraftWallet = ref(null)

    const draftSectionsByWallet = reactive({ multi: {}, single: {} })

    const pickDraftWalletWithContent = () => {
      if (editingStatusState.modalVisible && editingStatusState.isEditing) return null
      for (const wallet of WALLET_KEYS) {
        if (draftWalletHasContent(draftSectionsByWallet[wallet])) return wallet
      }
      return null
    }

    const setDraftModalType = async (val) => {
      if (val) {
        draftModalType.value = val
        spinVisible.value = true
        await nextTick() // 先讓 Vue 把 DOM patch 完

        // 再讓瀏覽器有機會重繪 spinVisible.value = false
        await new Promise(resolve => setTimeout(resolve, 0))
        draftModalVisible.value = true
        spinVisible.value = false
        return
      }
      // 僅關閉 visible，保留 draftModalType 直到 afterClose，避免關閉動畫期間右欄「更版說明」先消失
      draftModalVisible.value = false
      activeDraftWallet.value = null
    }
    const draftSection = computed(() => {
      if (activeDraftWallet.value) {
        return draftSectionsByWallet[activeDraftWallet.value] || {}
      }

      const pendingWallet = pickDraftWalletWithContent()
      if (pendingWallet) return draftSectionsByWallet[pendingWallet] || {}

      const wallet = getEditorRouteWallet() || selectedSectionState.wallet
      if (wallet) return draftSectionsByWallet[wallet] || {}
      return {}
    })

    watch(() => router.currentRoute.value.params.wallet, () => {
      if (!draftModalVisible.value) activeDraftWallet.value = null
    })

    const showPublishDraftModal = () => {
      const { wallet: draftWallet } = draftSection.value
      AntModal.confirm({
        title: h(
          'div',
          [
            h(
              'span',
              `${titleCase(draftWallet)} Wallet`,
            ),
          ],
        ),
        content: '有尚未發佈的草稿，請先發佈。',
      })
    }

    const showSaveChangesModal = () => {
      const modal = AntModal.confirm({
        title: '有尚未儲存的變更，請先 Save or Cancel。',
        content: '',
        okText: 'Cancel',
        cancelText: 'OK',
        onOk: () => modal.destroy(),
        onCancel: () => modal.destroy(),
      })
    }

    const createDocModalVisible = ref(false)

    const onChangeCreateDocModalVisible = (val) => {
      if (val && isDataChanged.value) {
        showSaveChangesModal()
        return
      }
      if (val && (!isEmpty(draftSection.value.updatedDataBySectionId) || hasUpdatedDoc(draftSection.value))) {
        showPublishDraftModal()
        return
      }
      createDocModalVisible.value = val
    }

    const watchCreateDocModalVisibleAndDocTitleOnce = watch([
      () => createDocModalVisible.value,
      () => selectedSectionState.docTitle,
    ], async ([newCreateDocModalVisible, newDocTitle], [oldCreateDocModalVisible, oldDocTitle]) => {
      console.log('[ApiDocSectionEditor] watch createDocModalVisible once', {
        newCreateDocModalVisible, newDocTitle, oldCreateDocModalVisible, oldDocTitle,
      })
      if (!newCreateDocModalVisible && !newDocTitle) return
      watchCreateDocModalVisibleAndDocTitleOnce()
    })

    const stagedSection = reactive({})

    // vue 3.4+ 才支援 { once: true }
    const watchSectionsDataOnce = watch(sectionsData, async (newVal, oldVal) => {
      console.log('[ApiDocSectionEditor] watch sectionsData', { newVal: cloneDeep(newVal), oldVal: cloneDeep(oldVal) })
      // console.trace('sectionsData 被更新的堆疊追蹤')

      // draft 須最後處理：getPublishedDocsDataByDocId 依賴 originSectionsByWallet 已建立
      const orderedSections = [...newVal].sort((a, b) => {
        if (a.sectionId === 'draft') return 1
        if (b.sectionId === 'draft') return -1
        return 0
      })

      for (const section of orderedSections) {
        const { docId, sectionId, wallet } = section
        // console.log(sectionId, 'section', section)
        if (sectionId.startsWith('staged')) continue

        if (sectionId === 'draft') {
          const clonedSection = cloneDeep(section)
          draftSectionsByWallet[wallet] = clonedSection
          if (draftWalletHasContent(clonedSection)) {
            applyUpdatedDocMapToDocsData(clonedSection.updatedDoc, docsDataByDocId, templateDataByTemplateId.value)
            console.log('[ApiDocSectionEditor] draft loaded', { wallet })
          }
          console.log('draftSectionsByWallet', draftSectionsByWallet)
        } else if (sectionId === 'ReleaseVer') {
          releaseVerSectionByDocId[docId] = cloneDeep(section)
        } else {
          sectionsByWallet[wallet][sectionId] = cloneDeep(section)
          originSectionsByWallet[wallet][sectionId] = cloneDeep(section)
        }
      }

      // 任一 wallet 有草稿即開彈窗；優先 multi（進入 /single/... 仍須提示 Multi 草稿）
      const draftModalWalletToOpen = pickDraftWalletWithContent()
      if (draftModalWalletToOpen) {
        activeDraftWallet.value = draftModalWalletToOpen
        await setDraftModalType('draft')
        console.log('set draft modal', {
          draftModalWalletToOpen,
          routeWallet: getEditorRouteWallet(),
          draftSection: draftSection.value,
          draftModalType: draftModalType.value,
        })
      }

      console.log('originSectionsByWallet', originSectionsByWallet)
      console.log('sectionsByWallet', sectionsByWallet)
      console.log('releaseVerSectionByDocId', releaseVerSectionByDocId)

      watchSectionsDataOnce()
    }, { deep: true })

    const applySelectSectionState = (sectionData) => {
      console.log('[ApiDocSectionEditor] applySelectSectionState', cloneDeep(sectionData))
      emptyObject(selectedSectionState)
      Object.assign(selectedSectionState, cloneDeep(sectionData))

      const wallet = sectionData.wallet || selectedSectionState.wallet || router.currentRoute.value.params.wallet
      const { sectionId } = selectedSectionState

      if (!sectionId || !wallet) {
        emptyObject(selectedFieldState)
        return
      }

      emptyObject(selectedFieldState)
      Object.assign(selectedFieldState, cloneDeep(fieldsByWallet[wallet]?.[sectionId] || {}))
    }

    const onSelectSectionState = (sectionData) => {
      const isOtherChildDocWhileUpdatedDocPending = () => {
        const pendingDocIds = getUpdatedDocIds(draftSection.value)
        if (!pendingDocIds.length || !sectionData?.docId) return false
        if (sectionData.docId.includes('master')) return false
        return !pendingDocIds.includes(sectionData.docId)
      }

      if (isOtherChildDocWhileUpdatedDocPending()) {
        showPublishDraftModal()
        return false
      }
      applySelectSectionState(sectionData)
      return true
    }

    const onClickManagement = (mngtData) => {
      // if (!readOnly.value && (!isEmpty(draftSection.value.updatedDataBySectionId) || hasUpdatedDoc(draftSection.value))) {
      //   showPublishDraftModal()
      //   return
      // }
      applySelectSectionState(mngtData)
    }

    const syncFieldsDocsForSection = (wallet, sectionId, docs) => {
      Object.values(fieldsByWallet[wallet][sectionId] || {}).forEach((scope) => {
        Object.values(scope).forEach((field) => {
          if (field.docs) {
            field.docs = field.docs.filter(docId => docs.includes(docId))
          }
          Object.keys(field.customizedVersion || {}).forEach((docId) => {
            if (!docs.includes(docId)) delete field.customizedVersion[docId]
          })
        })
      })
    }

    const getSectionDisplayLabel = (section) => {
      const label = `${section?.enTitle || ''} ${section?.cnTitle || ''}`.trim()
      return label || section?.sectionId || ''
    }

    /** 點擊加入的子區塊 + docs 尚無此子本的母區塊；若某層母區塊已有子本則不再往上 */
    const collectSectionIdsToAddDoc = (wallet, startSectionId, docId) => {
      const ids = []
      let cur = sectionsByWallet[wallet]?.[startSectionId]
      while (cur) {
        if (!(cur.docs || []).includes(docId)) {
          ids.push(cur.sectionId)
        }
        if (isRootParentSectionId(cur.parentSectionId)) break
        const parent = sectionsByWallet[wallet]?.[cur.parentSectionId]
        if (!parent) break
        if ((parent.docs || []).includes(docId)) break
        cur = parent
      }
      return ids
    }

    const collectAncestorSectionIdsToAddDoc = (wallet, childSectionId, docId) => {
      const child = sectionsByWallet[wallet]?.[childSectionId]
      if (!child || isRootParentSectionId(child.parentSectionId)) return []
      return collectSectionIdsToAddDoc(wallet, child.parentSectionId, docId)
    }

    const applyDocIdToSections = (wallet, sectionIds, docId) => {
      let anyChanged = false
      for (const sid of sectionIds) {
        const section = sectionsByWallet[wallet]?.[sid]
        if (!section || (section.docs || []).includes(docId)) continue

        section.docs = sortDocIds([...(section.docs || []), docId])
        syncFieldsDocsForSection(wallet, sid, section.docs)
        anyChanged = true
      }
      return anyChanged
    }

    const resyncSortOrdersForAddedDocs = (wallet, addedDocIds) => {
      const sectionIds = new Set()
      addedDocIds.forEach((docId) => {
        resyncDocCustomizedSortOrderByMasterOrder({
          wallet,
          docId,
          sectionsBySectionId: sectionsByWallet[wallet],
        }).forEach(id => sectionIds.add(id))
      })
      return [...sectionIds]
    }

    const syncSelectedSectionCustomizedSortOrders = (wallet, sectionId, addedDocIds) => {
      if (selectedSectionState.sectionId !== sectionId) return
      const live = sectionsByWallet[wallet]?.[sectionId]
      if (!live?.customizedVersion) return

      addedDocIds.forEach((docId) => {
        const sortOrder = live.customizedVersion[docId]?.sortOrder
        if (sortOrder === undefined) return
        if (!selectedSectionState.customizedVersion) selectedSectionState.customizedVersion = {}
        if (!selectedSectionState.customizedVersion[docId]) {
          selectedSectionState.customizedVersion[docId] = { docId }
        }
        selectedSectionState.customizedVersion[docId].sortOrder = sortOrder
      })
    }

    const propagateAddedDocsToAncestorSections = (wallet, childSectionId, addedDocIds) => {
      if (!addedDocIds?.length) return []

      const ancestorSectionIdsWithDocsAdded = new Set()
      const docLabels = []

      addedDocIds.forEach((docId) => {
        const ancestorIds = collectAncestorSectionIdsToAddDoc(wallet, childSectionId, docId)
        if (!ancestorIds.length) return

        docLabels.push(docsDataByDocId[docId]?.docShortTitle || docId)
        if (!applyDocIdToSections(wallet, ancestorIds, docId)) return

        ancestorIds.forEach(id => ancestorSectionIdsWithDocsAdded.add(id))
      })

      if (!ancestorSectionIdsWithDocsAdded.size || !docLabels.length) return []

      const parentLabels = [...ancestorSectionIdsWithDocsAdded]
        .map(sid => getSectionDisplayLabel(sectionsByWallet[wallet]?.[sid]))
        .filter(Boolean)
      const docLabel = docLabels.join('、')

      const parentLabelNodes = parentLabels.flatMap((label, idx) => {
        const nodes = [h('b', label)]
        if (idx < parentLabels.length - 1) nodes.push('、')
        return nodes
      })

      antMessage.info({
        content: h('span', [
          '已在此區塊的父區塊 ',
          ...parentLabelNodes,
          ' 的',
          h('br'),
          'Used In 欄位中自動加入 ',
          h('b', docLabel),
        ]),
      })

      return [...ancestorSectionIdsWithDocsAdded]
    }

    const finalizeAddedDocsToSections = (wallet, childSectionId, addedDocIds) => {
      if (!addedDocIds?.length) return

      const draftSectionIds = new Set()
      const ancestorIds = !isRootParentSectionId(sectionsByWallet[wallet]?.[childSectionId]?.parentSectionId)
        ? propagateAddedDocsToAncestorSections(wallet, childSectionId, addedDocIds)
        : []

      ancestorIds.forEach(id => draftSectionIds.add(id))
      resyncSortOrdersForAddedDocs(wallet, addedDocIds).forEach(id => draftSectionIds.add(id))

      if (!draftSectionIds.size) return

      upsertDraftSectionEntries({
        draftSection: draftSectionsByWallet[wallet],
        sectionsBySectionId: sectionsByWallet[wallet],
        sectionIds: [...draftSectionIds],
        skipPreserveSortOrderDocIds: addedDocIds,
        originSectionsBySectionId: originSectionsByWallet[wallet],
      })

      syncSelectedSectionCustomizedSortOrders(wallet, childSectionId, addedDocIds)
    }

    const onChangeSelectedSectionState = (key, value) => {
      console.log('[ApiDocSectionEditor] onChangeSelectedSectionState', key, value)
      // console.trace()
      if (key.includes('.')) {
        const keys = key.split('.')
        let current = selectedSectionState
        for (let i = 0; i < keys.length - 1; i += 1) {
          if (!current[keys[i]]) current[keys[i]] = {}
          current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = value
      } else {
        selectedSectionState[key] = value

        // 當 selectedSectionState.docs 變更時，更新 fieldsByWallet 的 docs 欄位
        if (key === 'docs') {
          const { wallet, sectionId } = selectedSectionState
          syncFieldsDocsForSection(wallet, sectionId, value)
        }
      }
    }

    const onCancel = async () => {
      const { sectionId, wallet: stateWallet } = selectedSectionState
      const wallet = stateWallet || router.currentRoute.value.params.wallet || 'multi'

      let sectionData = {}
      if (isReleaseVer.value) sectionData = releaseVerSectionByDocId[selectedSectionState.docId] || {}
      else if (selectedSectionState.docTitle) sectionData = docsDataByDocId[selectedSectionState.docId] || {}
      else sectionData = sectionsByWallet[wallet]?.[sectionId] || {}

      emptyObject(selectedSectionState)
      if (sectionData && !isEmpty(sectionData)) {
        Object.assign(selectedSectionState, cloneDeep(sectionData))
      }

      emptyObject(selectedFieldState)
      const fieldSource = isReleaseVer.value
        ? releaseVerFieldsByDocId[selectedSectionState.docId]
        : fieldsByWallet[wallet]?.[sectionId]
      Object.assign(selectedFieldState, cloneDeep(fieldSource || {}))

      antMessage.success('Changes have been reverted to previous state')
    }

    const onUseDraft = async (autoSelectTreeItem = false) => {
      console.log('onUseDraft', draftSection.value)
      const { wallet } = draftSection.value

      let firstSection
      Object.entries(draftSection.value.updatedDataBySectionId).forEach(([sectionId, { sectionData, fieldsData }], idx) => {
        if (sectionData) {
          sectionsByWallet[wallet][sectionId] = cloneDeep(sectionData)

          if (selectedSectionState.sectionId === sectionId && selectedSectionState.wallet === wallet) {
            emptyObject(selectedSectionState)
            Object.assign(selectedSectionState, cloneDeep(sectionData))
          }
        }

        if (fieldsData?.length) {
          fieldsData.forEach((fieldData) => {
            const { scope, fieldId, sectionIdFieldId } = fieldData
            if (!fieldsByWallet[wallet][sectionId]) fieldsByWallet[wallet][sectionId] = {}
            if (!fieldsByWallet[wallet][sectionId][scope]) fieldsByWallet[wallet][sectionId][scope] = {}
            fieldsByWallet[wallet][sectionId][scope][fieldId] = cloneDeep(fieldData)

            if (selectedFieldState[scope]?.[fieldId]?.sectionIdFieldId === sectionIdFieldId && selectedFieldState[scope]?.[fieldId]?.wallet === wallet) {
              emptyObject(selectedFieldState[scope][fieldId])
              Object.assign(selectedFieldState[scope][fieldId], cloneDeep(fieldData))
            }
          })
        }

        if (idx === 0) {
          if (sectionData) firstSection = sectionData
          else firstSection = sectionsBySectionId.value[sectionId]
        }
      })

      applyUpdatedDocMapToDocsData(draftSection.value.updatedDoc, docsDataByDocId, templateDataByTemplateId.value)

      // 套用不同 wallet 的草稿後，避免沿用舊 wallet 的 field 選取狀態
      if (selectedSectionState.wallet !== wallet) {
        emptyObject(selectedFieldState)
      } else if (selectedSectionState.sectionId && !isReleaseVer.value && !selectedSectionState.docTitle) {
        const latestFields = fieldsByWallet[wallet]?.[selectedSectionState.sectionId] || {}
        emptyObject(selectedFieldState)
        Object.assign(selectedFieldState, cloneDeep(latestFields))
      }

      if (autoSelectTreeItem && isEditorV3BaseRoute()) {
        if (hasUpdatedDoc(draftSection.value)) {
          activeTreeTab.value = 'doc'
          nextTick(() => {
            autoSelectDraftSection.value = autoSelectTreeItem
          })
        } else if (firstSection?.sectionId) {
          const targetPath = `/api-doc/editor/${wallet}/${firstSection.sectionId}`
          if (router.currentRoute.value.path !== targetPath) {
            router.replace(targetPath)
            nextTick(() => {
              autoSelectDraftSection.value = autoSelectTreeItem
            })
          }
        }
      }

      await setDraftModalType(false)
    }

    /** 捨棄草稿：將草稿內所有 section／field 還原為 origin（含 EditDoc 一鍵加入 Used In） */
    const revertDraftWalletSectionsFromOrigin = (wallet, draftSnapshot) => {
      const updated = draftSnapshot?.updatedDataBySectionId || {}
      const sectionIds = Object.keys(updated)
      if (!wallet || !sectionIds.length) return

      for (const sectionId of sectionIds) {
        const originSection = originSectionsByWallet[wallet]?.[sectionId]
        if (originSection) {
          sectionsByWallet[wallet][sectionId] = cloneDeep(originSection)
        }
        const originFields = originFieldsByWallet[wallet]?.[sectionId]
        if (originFields) {
          fieldsByWallet[wallet][sectionId] = cloneDeep(originFields)
        }
      }

      if (
        selectedSectionState.wallet === wallet
        && selectedSectionState.sectionId
        && !selectedSectionState.docTitle
        && sectionIds.includes(selectedSectionState.sectionId)
      ) {
        const { sectionId } = selectedSectionState
        emptyObject(selectedSectionState)
        Object.assign(selectedSectionState, cloneDeep(originSectionsByWallet[wallet]?.[sectionId] || {}))
        emptyObject(selectedFieldState)
        Object.assign(selectedFieldState, cloneDeep(originFieldsByWallet[wallet]?.[sectionId] || {}))
      }
    }

    const onDiscardDraft = async (fromButton = false) => {
      console.log('onDiscardDraft', { fromButton, isDev: isDev.value })
      const onOk = async () => {
        const messageKey = Date.now()
        const draftSnapshot = cloneDeep(draftSection.value)

        console.log('onDiscardDraft', { draftSection: draftSnapshot })
        const { data, message } = await updateSection({
          ...draftSnapshot,
          updatedDataBySectionId: {},
          updatedDoc: null,
        })
        console.log('update draft section res', { data, message })

        if (data) {
          await setDraftModalType(false)
          antMessage.success({
            key: messageKey,
            content: '成功清空草稿',
          })

          revertDraftWalletSectionsFromOrigin(draftSnapshot.wallet, draftSnapshot)

          // updatedDoc 還原
          getUpdatedDocIds(draftSnapshot).forEach((docId) => {
            console.log('onDiscardDraft', docId, { originalDocsDataByDocId: originalDocsDataByDocId.value })
            if (!docId || !originalDocsDataByDocId.value[docId]) return
            emptyObject(docsDataByDocId[docId])
            Object.assign(docsDataByDocId[docId], cloneDeep(originalDocsDataByDocId.value[docId]))

            // 如果頁面在 EditDoc
            if (selectedSectionState.docId === docId && selectedSectionState.docTitle) {
              emptyObject(selectedSectionState)
              Object.assign(selectedSectionState, cloneDeep(docsDataByDocId[docId]))
            }
          })

          draftSectionsByWallet[data.wallet] = data
        } else {
          antMessage.error({
            key: messageKey,
            duration: 10,
            content: `Failed to reset draft section: ${message}`,
          })
        }
      }

      if (fromButton && !isDev.value) {
        AntModal.confirm({
          title: '確認捨棄草稿？',
          content: '確認捨棄後，草稿將會被清空，無法復原。',
          onOk,
        })
        return
      }

      onOk()
    }

    const formRules = reactive({
      enTitle: [
        { required: true, message: 'Required' },
      ],
      cnTitle: [
        { required: true, message: 'Required' },
      ],
      path: [
        { required: true, message: 'Required' },
        {
          pattern: /^\/[A-Za-z0-9/{}_/]*$/,
          message: 'Please enter the correct format',
        },
      ],
      operationId: [
        { required: true, message: 'Required' },
        {
          pattern: /^[A-Za-z0-9_-]*$/,
          message: 'Please enter the correct format',
        },
      ],
    })

    watch(() => selectedSectionState.path, (newPath, oldPath) => {
      if (newPath == null || newPath === oldPath) return
      const nextOperationId = newPath.split('/').filter(Boolean).pop().replace(/\W|callbackURL_/g, '') || ''
      if (selectedSectionState.operationId !== nextOperationId) selectedSectionState.operationId = nextOperationId
    })

    const { validate, validateInfos } = useForm(selectedSectionState, formRules)

    const stripFieldPublishFlags = (field) => {
      const { added, deleted, ...rest } = field
      return rest
    }

    const stageDraftForDocPlatformChange = async ({
      docId, wallet, oldDocData, newDocData,
    }) => {
      const allPlatforms = entirePlatformOptions.value.map(({ value }) => value)
      const allDocs = Object.keys(docsDataByDocId)
      const editorUsername = localStorage.getItem('editor-username')
      const updatedTime = moment().format('YYYY/MM/DD HH:mm')
      let hasStagedChanges = false

      const ensureDraftSectionEntry = (sectionId) => {
        if (!draftSection.value.updatedDataBySectionId[sectionId]) {
          draftSection.value.updatedDataBySectionId[sectionId] = { editorUsername, updatedTime, fieldsData: [] }
        } else {
          draftSection.value.updatedDataBySectionId[sectionId].updatedTime = updatedTime
          draftSection.value.updatedDataBySectionId[sectionId].editorUsername = editorUsername
          if (!draftSection.value.updatedDataBySectionId[sectionId].fieldsData) {
            draftSection.value.updatedDataBySectionId[sectionId].fieldsData = []
          }
        }
      }

      for (const { sectionId } of (sectionsDataByDocId.value[docId] || [])) {
        if (sectionId === 'ReleaseVer') continue

        const scopes = originFieldsByWallet[wallet]?.[sectionId]
        if (!scopes) continue

        for (const scopeFields of Object.values(scopes)) {
          for (const originField of Object.values(scopeFields)) {
            const liveField = fieldsByWallet[wallet][sectionId]?.[originField.scope]?.[originField.fieldId] || originField
            const fieldForDocVisibility = {
              ...liveField,
              ...(liveField.customizedVersion?.[docId] || {}),
            }

            const publishFlags = getFieldPublishFlagsForDocPlatformChange({
              fieldData: fieldForDocVisibility,
              docId,
              oldDocData,
              newDocData,
              allPlatforms,
              allDocs,
            })
            if (!publishFlags) continue

            ensureDraftSectionEntry(sectionId)
            const { fieldsData } = draftSection.value.updatedDataBySectionId[sectionId]
            const fieldToStage = cloneDeep(liveField)
            const idxInDraft = fieldsData.findIndex(f => f.fieldId === fieldToStage.fieldId && f.scope === fieldToStage.scope)
            if (idxInDraft > -1) {
              const existing = fieldsData[idxInDraft]
              if (!publishFlags && !isEqual(stripFieldPublishFlags(existing), stripFieldPublishFlags(originField))) continue
              fieldsData[idxInDraft] = fieldToStage
            } else {
              fieldsData.push(fieldToStage)
            }
            hasStagedChanges = true
          }
        }
      }

      return hasStagedChanges
    }

    const ensureWalletDraftSection = (wallet) => {
      let draft = draftSectionsByWallet[wallet]
      if (!draft?.docId) {
        const masterDocId = Object.keys(docsDataByDocId).find(
          docId => docId.includes('master') && docsDataByDocId[docId]?.wallet === wallet,
        )
        draft = {
          docId: masterDocId,
          sectionId: 'draft',
          wallet,
          updatedDataBySectionId: {},
        }
        draftSectionsByWallet[wallet] = draft
      }
      if (!draft.updatedDataBySectionId) draft.updatedDataBySectionId = {}
      return draft
    }

    const collectDraftPayloadFromAllWallets = () => {
      const updatedDocByDocId = {}
      const updatedDataBySectionId = {}
      for (const wallet of WALLET_KEYS) {
        const draft = draftSectionsByWallet[wallet]
        if (!draft) continue
        Object.assign(updatedDocByDocId, normalizeUpdatedDocByDocId(draft.updatedDoc))
        Object.assign(updatedDataBySectionId, draft.updatedDataBySectionId || {})
      }
      return { updatedDocByDocId, updatedDataBySectionId }
    }

    /** 本次發佈 payload 實際涉及的 wallet（用於發佈後只寫回相關草稿） */
    const collectPublishAffectedWallets = ({
      updatedDocByDocId = {},
      updatedDataBySectionId = {},
      publishedDocsDataByDocId = {},
    }) => {
      const wallets = new Set()
      Object.keys(updatedDocByDocId).forEach((docId) => {
        const wallet = getDocWallet(docId)
        if (wallet) wallets.add(wallet)
      })
      Object.values(updatedDataBySectionId).forEach(({ sectionData, fieldsData }) => {
        if (sectionData?.wallet) wallets.add(sectionData.wallet)
        if (fieldsData) {
          fieldsData.forEach((f) => {
            if (f.wallet) wallets.add(f.wallet)
          })
        }
      })
      Object.values(publishedDocsDataByDocId).forEach((publishedDocData) => {
        if (!publishedDocData?.shouldRelease) return
        const wallet = publishedDocData?.docData?.wallet
        if (wallet) wallets.add(wallet)
      })
      return wallets
    }

    const handleWriteDraftSection = async (wallet) => {
      const messageKey = Date.now()
      // selectedSectionState 位於 management 又拖曳排序時，又或者 selectedSectionState 位於 multi wallet 又拖曳排序 single 排序時

      const targetDraft = wallet ? draftSectionsByWallet[wallet] : draftSection.value
      if (!targetDraft?.docId) return

      const { data, message } = await updateSection(targetDraft)
      console.log('update draft section res', { data, message })

      if (data) {
        antMessage.success({
          key: messageKey,
          content: '成功更新草稿',
        })
      } else {
        antMessage.error({
          key: messageKey,
          duration: 10,
          content: `Failed to save section: ${message}`,
        })
      }
    }

    const onAddDocToSectionFromEditDoc = async (startSectionId) => {
      if (isDataChanged.value) {
        antMessage.warning('請先點擊下方 Save 儲存子本設定後，再執行一鍵加入')
        return
      }

      const { docId, wallet } = selectedSectionState
      if (!docId || !wallet || !startSectionId) return

      const sectionIds = collectSectionIdsToAddDoc(wallet, startSectionId, docId)
      if (!applyDocIdToSections(wallet, sectionIds, docId)) return

      const sortOrderChangedIds = resyncDocCustomizedSortOrderByMasterOrder({
        wallet,
        docId,
        sectionsBySectionId: sectionsByWallet[wallet],
      })

      upsertDraftSectionEntries({
        draftSection: draftSectionsByWallet[wallet],
        sectionsBySectionId: sectionsByWallet[wallet],
        sectionIds: [...new Set([...sectionIds, ...sortOrderChangedIds])],
        skipPreserveSortOrderDocIds: [docId],
        originSectionsBySectionId: originSectionsByWallet[wallet],
      })

      await handleWriteDraftSection()
      antMessage.success('已將此子本加入該區塊的 Used In 欄位')
    }

    const onRemoveDocFromSectionFromEditDoc = async (sectionId) => {
      if (isDataChanged.value) {
        antMessage.warning('請先點擊下方 Save 儲存子本設定後，再執行一鍵移除')
        return
      }

      const { docId, wallet } = selectedSectionState
      if (!docId || !wallet || !sectionId) return

      const section = sectionsByWallet[wallet]?.[sectionId]
      if (!section) return

      const prevDocs = section.docs || []
      if (!prevDocs.includes(docId)) return

      section.docs = sortDocIds(prevDocs.filter(id => id !== docId))
      syncFieldsDocsForSection(wallet, sectionId, section.docs)

      const sortOrderChangedIds = resyncDocCustomizedSortOrderByMasterOrder({
        wallet,
        docId,
        sectionsBySectionId: sectionsByWallet[wallet],
      })

      upsertDraftSectionEntries({
        draftSection: draftSectionsByWallet[wallet],
        sectionsBySectionId: sectionsByWallet[wallet],
        sectionIds: [...new Set([sectionId, ...sortOrderChangedIds])],
      })

      await handleWriteDraftSection()
      antMessage.success('已將此子本從該區塊的 Used In 欄位移除')
    }

    const publishButtonDisabled = computed(() => {
      const { updatedDataBySectionId = {} } = draftSection.value
      return isEmpty(updatedDataBySectionId) && !hasUpdatedDoc(draftSection.value)
    })

    /** 補齊 ApiDoc_Fields 主鍵與 ReleaseVer row 必填欄位（修正舊版 #$row#、缺 docId） */
    const normalizeReleaseVerFieldForWrite = (field, targetDocId) => {
      const payload = cloneDeep(field)
      delete payload.added
      delete payload.deleted
      delete payload.key
      delete payload.isNew

      const { fieldId } = payload
      if (!fieldId) return null

      payload.docId = payload.docId || targetDocId
      payload.sectionId = payload.sectionId || 'ReleaseVer'
      payload.scope = payload.scope || 'row'
      payload.parentFieldId = payload.parentFieldId ?? 'ROOT'
      payload.level = payload.level ?? 0

      const sidf = payload.sectionIdFieldId
      if (!sidf || String(sidf).includes('$row')) {
        payload.sectionIdFieldId = `ReleaseVer#row#${fieldId}`
      }

      if (!payload.wallet) {
        const docWallet = docsDataByDocId[targetDocId]?.wallet
        if (docWallet) payload.wallet = docWallet
        else {
          const withWallet = Object.values(releaseVerFieldsByDocId[targetDocId]?.row || {})
            .find(f => f && !f.deleted && f.wallet)
          if (withWallet?.wallet) payload.wallet = withWallet.wallet
        }
      }

      return payload
    }

    const syncReleaseVerFieldStateFromStore = (targetDocId) => {
      if (!releaseVerFieldsByDocId[targetDocId]) releaseVerFieldsByDocId[targetDocId] = {}
      emptyObject(selectedFieldState)
      Object.assign(selectedFieldState, cloneDeep(releaseVerFieldsByDocId[targetDocId]))
    }

    /** batch-delete 回傳的 fieldsToUpdate：同層刪除後重排的 sortOrder */
    const applyReleaseVerFieldsToUpdate = (targetDocId, fieldsToUpdate = []) => {
      fieldsToUpdate.forEach((f) => {
        const {
          scope, fieldId, sortOrder, docId: fieldDocId,
        } = f
        const d = fieldDocId || targetDocId
        const stored = releaseVerFieldsByDocId[d]?.[scope]?.[fieldId]
        if (!stored) return
        stored.sortOrder = sortOrder
        if (selectedFieldState[scope]?.[fieldId]) {
          selectedFieldState[scope][fieldId].sortOrder = sortOrder
        }
      })
    }

    const removeReleaseVerFieldsByDeleteKeys = (targetDocId, deleteData = []) => {
      deleteData.forEach(({ sectionIdFieldId }) => {
        if (!sectionIdFieldId) return
        const [, scope, fieldId] = sectionIdFieldId.split('#')
        if (!scope || !fieldId) return
        if (releaseVerFieldsByDocId[targetDocId]?.[scope]?.[fieldId]) {
          delete releaseVerFieldsByDocId[targetDocId][scope][fieldId]
        }
        if (selectedFieldState[scope]?.[fieldId]) {
          delete selectedFieldState[scope][fieldId]
        }
      })
    }

    const mergeReleaseVerWrittenFields = (targetDocId, writtenFields = []) => {
      if (!releaseVerFieldsByDocId[targetDocId]) releaseVerFieldsByDocId[targetDocId] = {}
      writtenFields.forEach((f) => {
        const { scope, fieldId } = f
        if (!releaseVerFieldsByDocId[targetDocId][scope]) releaseVerFieldsByDocId[targetDocId][scope] = {}
        const cleaned = cloneDeep(f)
        delete cleaned.added
        delete cleaned.deleted
        releaseVerFieldsByDocId[targetDocId][scope][fieldId] = cleaned
      })
      syncReleaseVerFieldStateFromStore(targetDocId)
    }

    const getActivityLogSizeLimitKb = (message) => {
      const text = String(message || '')
      if (/Item size has exceeded/i.test(text)) return 400
      if (/PayloadTooLarge|entity too large/i.test(text)) return 1024
      return null
    }

    const notifyPublishActivityLogFailed = (message, sizeBytes) => {
      const text = message || 'unknown error'
      const limitKb = getActivityLogSizeLimitKb(text)
      const sizeSuffix = limitKb != null
        && Number.isFinite(sizeBytes)
        && sizeBytes >= 0
        ? `（${Math.round(sizeBytes / 1024)} KB > ${limitKb} KB）`
        : ''
      antMessage.error(`發佈已完成，但 Activity Log 寫入失敗：${text}${sizeSuffix}`)
    }

    async function recordPublishActivityLog({
      publishedDocsDataByDocId,
      updatedDataBySectionId,
      causedBy,
    }) {
      let jsonBytes = 0
      try {
        const editorUsername = localStorage.getItem('editor-username') || 'unknown'
        if (isEmpty(publishedDocsDataByDocId)) {
          console.error('[ActivityLog] skip publish record: empty snapshot')
          antMessage.warning('發佈已完成，但未寫入 Activity Log（快照為空）')
          return
        }

        const fallbackWallet = Object.values(updatedDataBySectionId || {}).find(s => s?.sectionData?.wallet)?.sectionData?.wallet
          || 'multi'
        const {
          wallet,
          target,
          masterDocId,
          masterDocIds,
        } = resolvePublishLogTarget(publishedDocsDataByDocId, fallbackWallet)
        const publishedAt = moment().format('YYYY/MM/DD HH:mm')

        const detail = buildPublishActivityDetail({
          wallet,
          publishedDocsDataByDocId,
          docsDataByDocId,
          masterDocId,
          masterDocIds,
          editorUsername,
          publishedAt,
          causedBy,
        })

        const payload = {
          action: ACTIVITY_ACTIONS.PUBLISH,
          editorUsername,
          wallet,
          target,
          detail,
        }
        jsonBytes = new TextEncoder().encode(JSON.stringify(payload)).length
        if (jsonBytes >= 300 * 1024) {
          console.warn('[ActivityLog] large publish payload', {
            jsonBytes,
            kb: Math.round(jsonBytes / 1024),
            docs: Object.keys(publishedDocsDataByDocId),
          })
        }

        const { data, message, jsonBytes: responseJsonBytes } = await createActivityLog(payload)
        const sizeBytes = Number.isFinite(responseJsonBytes) ? responseJsonBytes : jsonBytes
        if (!data) {
          console.error('[ActivityLog] publish record failed', message, { jsonBytes: sizeBytes })
          notifyPublishActivityLogFailed(message, sizeBytes)
        }
      } catch (err) {
        console.error('[ActivityLog] publish record error', err)
        notifyPublishActivityLogFailed(err.message || err, jsonBytes)
      }
    }

    async function recordReleaseVerPublishIfChanged({
      docId,
      beforeRowsById,
      afterRowsById,
    }) {
      const sectionData = releaseVerSectionByDocId[docId]
      const entry = buildReleaseVerPublishSnapshotEntry({
        docId,
        docsDataByDocId,
        sectionData,
        fieldsDiffData: buildReleaseVerRowDiffs({
          beforeRowsById,
          afterRowsById,
          sectionData,
        }),
      })
      if (!entry) return
      await recordPublishActivityLog({
        publishedDocsDataByDocId: { [docId]: entry },
        updatedDataBySectionId: {},
      })
    }

    function collectReleaseVerPublishEntries(originRowsByDocId = {}) {
      const entries = []
      Object.entries(originRowsByDocId).forEach(([docId, beforeRowsById]) => {
        const sectionData = releaseVerSectionByDocId[docId]
        const entry = buildReleaseVerPublishSnapshotEntry({
          docId,
          docsDataByDocId,
          sectionData,
          fieldsDiffData: buildReleaseVerRowDiffs({
            beforeRowsById,
            afterRowsById: releaseVerFieldsByDocId[docId]?.row || {},
            sectionData,
          }),
        })
        if (entry) entries.push(entry)
      })
      return entries
    }

    const onSaveToDraft = async () => {
      spinVisible.value = true
      const { docId, sectionId, wallet } = selectedSectionState

      if (isReleaseVer.value) {
        const originReleaseVerRows = cloneDeep(releaseVerFieldsByDocId[docId]?.row || {})
        const pendingReleaseVerRows = cloneDeep(selectedFieldState.row || {})
        try {
          const batchWriteFieldsData = []
          const batchDeleteFieldsData = []

          Object.values(selectedFieldState.row || {}).forEach(({ added, deleted, ...field }) => {
            if (deleted) {
              batchDeleteFieldsData.push({
                docId: field.docId || docId,
                sectionIdFieldId: field.sectionIdFieldId,
              })
            } else {
              const payload = normalizeReleaseVerFieldForWrite(field, docId)
              if (payload) batchWriteFieldsData.push(payload)
            }
          })

          if (batchDeleteFieldsData.length) {
            const deleteRes = await batchDeleteFields(batchDeleteFieldsData)
            if (!deleteRes?.data) {
              antMessage.error({
                duration: 10,
                content: `Failed to delete Release Version fields: ${deleteRes?.message || deleteRes}`,
              })
              return
            }

            const {
              deleteData = [],
              fieldsToUpdate = [],
              missing = [],
            } = deleteRes.data

            if (missing.length) {
              antMessage.warning(`部分欄位已不存在，略過 ${missing.length} 筆`)
            }

            removeReleaseVerFieldsByDeleteKeys(docId, deleteData)
            applyReleaseVerFieldsToUpdate(docId, fieldsToUpdate)
          }

          if (batchWriteFieldsData.length) {
            const writeRes = await batchWriteFields(batchWriteFieldsData)
            if (!writeRes?.data) {
              antMessage.error({
                duration: 10,
                content: `Failed to update Release Version: ${writeRes?.message || writeRes}`,
              })
              return
            }
            mergeReleaseVerWrittenFields(docId, writeRes.data)
          } else if (batchDeleteFieldsData.length) {
            syncReleaseVerFieldStateFromStore(docId)
          }

          if (batchWriteFieldsData.length || batchDeleteFieldsData.length) {
            antMessage.success('成功更新發佈版本')
            applyDocsModalVisible.value = false
            await recordReleaseVerPublishIfChanged({
              docId,
              beforeRowsById: originReleaseVerRows,
              afterRowsById: pendingReleaseVerRows,
            })
          }
        } finally {
          spinVisible.value = false
        }
        return
      }
      const toUpdateSections = []

      if (selectedSectionState.docTitle) { // 編輯子本
        // const { noApiSection } = editDocRef.value.masterTreeTransferRef
        // if (noApiSection) return

        // const { docId, addTreeData, addSectionsData } = selectedSectionState
        // if (docsDataByDocId[docId].docTitle !== selectedSectionState.docTitle) {
        //   console.log('selectedSectionState.docTitle', selectedSectionState.docTitle, 'selectedSectionState.docTitle', selectedSectionState.docTitle)
        //   if (!stagedSection.updatedDoc) stagedSection.updatedDoc = {}
        //   stagedSection.updatedDoc.docTitle = selectedSectionState.docTitle
        //   docsDataByDocId[docId].docTitle = selectedSectionState.docTitle
        // }

        const hasChangedKeys = []
        const oldDocData = cloneDeep(originalDocsDataByDocId.value[docId] || docsDataByDocId[docId])
        const docEditableKeys = ['currency', 'subName', 'excludedPlatforms', 'platforms']
        const docKeys = ['docId', 'domain', 'route', 'templateId', 'wallet', ...docEditableKeys]

        docEditableKeys.forEach((key) => {
          if (docsDataByDocId[docId][key] == null && selectedSectionState[key] == null) return // undefined vs. null 視為未變更

          const hasChanged = !isEqual(docsDataByDocId[docId][key], selectedSectionState[key])
          if (hasChanged) {
            hasChangedKeys.push(key)
            console.log(`docsDataByDocId[${docId}].${key}`, docsDataByDocId[docId][key], `selectedSectionState.${key}`, selectedSectionState[key])
            docsDataByDocId[docId][key] = cloneDeep(selectedSectionState[key])
            deriveDocDataWithTitlesAndTemplateData(docsDataByDocId[docId], templateDataByTemplateId.value)
          }
        })

        if (hasChangedKeys.length) {
          const newDocData = cloneDeep(docsDataByDocId[docId])

          setUpdatedDocEntry(
            draftSection.value,
            docId,
            docKeys.reduce((acc, key) => {
              acc[key] = newDocData[key]
              return acc
            }, {}),
          )

          await stageDraftForDocPlatformChange({
            docId,
            wallet,
            oldDocData,
            newDocData,
          })
        }
      } else {
        console.log('onSaveToDraft', sectionId, {
          isDataChanged: isDataChanged.value,
          isSectionStateEqual: isSectionStateEqual.value,
          isFieldStateEqual: isFieldStateEqual.value,
          updatedDataBySectionId: draftSection.value.updatedDataBySectionId[sectionId],
        })

        if (isDataChanged.value) {
          if (!isSectionStateEqual.value) {
            const prevDocs = [...(sectionsByWallet[wallet][sectionId]?.docs || [])]
            const sectionData = cleanSection(cloneDeep(selectedSectionState))
            upsertDraftSectionEntries({
              draftSection: draftSectionsByWallet[wallet],
              sectionsBySectionId: sectionsByWallet[wallet],
              sectionIds: sectionId,
              sectionDataMap: { [sectionId]: sectionData },
              syncToSectionsByWallet: true,
            })

            const addedDocIds = (sectionData.docs || []).filter(doc => !prevDocs.includes(doc))
            if (addedDocIds.length) {
              finalizeAddedDocsToSections(wallet, sectionId, addedDocIds)
            }
          } else {
            upsertDraftSectionEntries({
              draftSection: draftSectionsByWallet[wallet],
              sectionsBySectionId: sectionsByWallet[wallet],
              sectionIds: sectionId,
              touchMetaOnly: true, // 僅 field 變更：只更新 updatedTime / editorUsername，不覆寫 sectionData
            })
          }

          if (!isFieldStateEqual.value) {
            if (!draftSection.value.updatedDataBySectionId[sectionId].fieldsData) draftSection.value.updatedDataBySectionId[sectionId].fieldsData = []

            Object.entries(selectedFieldState).forEach(([scope, scopeFields]) => {
              Object.entries(scopeFields).forEach(([fieldId, field]) => {
                if (isEqual(field, originFieldsByWallet[wallet][sectionId]?.[scope]?.[fieldId])) return

                if (!fieldsByWallet[wallet][sectionId]) fieldsByWallet[wallet][sectionId] = {}
                if (!fieldsByWallet[wallet][sectionId][scope]) fieldsByWallet[wallet][sectionId][scope] = {}
                fieldsByWallet[wallet][sectionId][scope][fieldId] = cloneDeep(field)

                const idxInDraft = draftSection.value.updatedDataBySectionId[sectionId].fieldsData.findIndex(f => f.fieldId === fieldId)
                console.log('idxInDraft', idxInDraft, field)
                if (idxInDraft > -1) {
                  draftSection.value.updatedDataBySectionId[sectionId].fieldsData[idxInDraft] = field
                } else {
                  draftSection.value.updatedDataBySectionId[sectionId].fieldsData.push(field)
                }
              })
            })
          }
        }

        applyDocsModalVisible.value = false
      }
      console.log('onSaveToDraft toUpdateSections', toUpdateSections)

      await handleWriteDraftSection()

      spinVisible.value = false
    }

    const onApplyDocsModalOk = () => onSaveToDraft()

    const onSave = async () => {
      try {
        const blockSaveWithFocus = async (sectionRef) => {
          const blocked = await sectionRef?.focusTableValidationErrorIfNeeded?.()
            ?? await sectionRef?.focusExampleFormatErrorIfNeeded?.()
          if (!blocked) return false

          const refocusValidationError = async () => {
            const refocus = sectionRef?.refocusTableValidationErrorIfNeeded
              || sectionRef?.refocusExampleFormatErrorIfNeeded
            if (refocus) await refocus()
          }
          await refocusValidationError()
          antMessage.error('Please enter the correct format')
          await nextTick()
          requestAnimationFrame(() => { refocusValidationError() })
          setTimeout(() => { refocusValidationError() }, 200)
          setTimeout(() => { refocusValidationError() }, 500)
          return true
        }

        if (selectedSectionState.sectionType === 'api') {
          if (await blockSaveWithFocus(apiSectionRef.value)) return
        } else if (selectedSectionState.sectionType === 'appendix') {
          if (await blockSaveWithFocus(appendixSectionRef.value)) return
        } else if (isReleaseVer.value) {
          if (await blockSaveWithFocus(releaseVerSectionRef.value)) return
        }

        await validate()
        if (selectedSectionState.sectionType) { // EditDoc 無 sectionType
          applyDocsModalVisible.value = true
        } else {
          await onSaveToDraft()
        }
      } catch (err) {
        console.error(err)
      }
    }

    // const versionHistory = []

    // console.log('versionHistoryWithRowId', versionHistory.map(row => ({
    //   rowId: new ShortUniqueId({ length: 10 }).rnd(),
    //   ...row,
    // })))

    const onChangeManagementData = (entityType, data) => {
      managementData[entityType] = data
    }

    const onPublish = async ({ causedBy } = {}) => {
      const batchWriteSectionsData = []
      const batchWriteFieldsData = []
      const batchDeleteFieldsData = []

      const { updatedDocByDocId, updatedDataBySectionId } = collectDraftPayloadFromAllWallets()
      const publishedSnapshot = enrichPublishedDocsWithUpdatedDocFieldDiffs(
        draftChangeRef.value?.publishedDocsDataByDocId
          ? cloneDeep(draftChangeRef.value.publishedDocsDataByDocId)
          : {},
        {
          updatedDocByDocId,
          // 須在 updateDoc 前抓 origin，否則發佈後 origin 已變新值
          originalDocsDataByDocId: originalDocsDataByDocId.value,
          docsDataByDocId,
          platformsMap: entirePlatformsMap.value,
        },
      )
      const publishAffectedWallets = collectPublishAffectedWallets({
        updatedDocByDocId,
        updatedDataBySectionId,
        publishedDocsDataByDocId: publishedSnapshot,
      })

      // 發佈批次寫入略過自動 Activity Log（改由下方統一寫入 publish 一筆）
      axios.defaults.headers.common['X-Skip-Activity-Log'] = '1'
      try {
        for (const [pendingDocId, updatedDocData] of Object.entries(updatedDocByDocId)) {
          const { data, message } = await updateDoc({ ...updatedDocData, docId: pendingDocId })
          console.log('onPublish updateDoc res', pendingDocId, { data, message })
          if (data) {
            Object.assign(docsDataByDocId[data.docId], data)
            Object.assign(originalDocsDataByDocId.value[data.docId], data)
            antMessage.success(`Update [${getDocShortTitle(data, templateDataByTemplateId.value)}] successfully`)
          } else {
            antMessage.error({
              duration: 10,
              content: `Failed to update doc [${pendingDocId}]: ${message}`,
            })
            return
          }
        }
        if (Object.keys(updatedDocByDocId).length) {
          publishAffectedWallets.forEach((wallet) => {
            if (draftSectionsByWallet[wallet]?.updatedDoc) draftSectionsByWallet[wallet].updatedDoc = {}
          })
        }

        Object.values(updatedDataBySectionId).forEach(({ sectionData, fieldsData }) => {
          if (sectionData) batchWriteSectionsData.push(sectionData)

          if (fieldsData?.length) {
            fieldsData.forEach((f) => {
              if (f.deleted) {
                batchDeleteFieldsData.push({
                  docId: f.docId,
                  sectionIdFieldId: f.sectionIdFieldId,
                })
              } else {
                delete f.added
                batchWriteFieldsData.push(f)
              }
            })
          }
        })

        const originReleaseVerRowsByDocId = {}
        Object.entries(publishedSnapshot).forEach(([docId, publishedDocData]) => {
          if (!publishedDocData || docId.includes('master')) return

          const {
            version, releaseContent: content, updateDate, shouldRelease,
            docData: { wallet },
          } = publishedDocData
          if (!shouldRelease) return

          originReleaseVerRowsByDocId[docId] = cloneDeep(releaseVerFieldsByDocId[docId]?.row || {})
          const fieldId = new ShortUniqueId({ length: 10 }).rnd()

          batchWriteFieldsData.push(
            {
              docId,
              sectionId: 'ReleaseVer',
              sectionIdFieldId: `ReleaseVer#row#${fieldId}`,
              parentFieldId: 'ROOT',
              fieldId,
              scope: 'row',
              version,
              content,
              wallet,
              updateDate,
              level: 0,
              sortOrder: 0,
            },
            ...Object.values(releaseVerFieldsByDocId[docId]?.row || {}).map(({ sortOrder, ...f }) => ({
              ...f,
              sortOrder: sortOrder + 1,
            })),
          )
        })
        console.log('onPublish', { batchWriteSectionsData, batchWriteFieldsData, batchDeleteFieldsData })

        if (batchDeleteFieldsData.length) {
          const { data, message } = await batchDeleteFields(batchDeleteFieldsData)
          console.log('onPublish batchDeleteFieldsData res', { data, message })
          if (data) {
            antMessage.success('Batch delete fields successfully')
            const { deleteData = [], fieldsToUpdate = [] } = data
            if (deleteData.length) {
              deleteData.forEach(({ sectionIdFieldId, docId }) => {
                const wallet = getDocWallet(docId)
                if (!wallet) return
                const [sectionId, scope, fieldId] = sectionIdFieldId.split('#')
                delete fieldsByWallet[wallet]?.[sectionId]?.[scope]?.[fieldId]
                delete originFieldsByWallet[wallet]?.[sectionId]?.[scope]?.[fieldId]
                delete draftSectionsByWallet[wallet]?.updatedDataBySectionId?.[sectionId]
                if (selectedSectionState.sectionId === sectionId) delete selectedFieldState?.[scope]?.[fieldId]
              })
            }

            fieldsToUpdate.forEach((f) => {
              const {
                sectionId, fieldId, scope, wallet,
              } = f
              if (!fieldsByWallet[wallet][sectionId]) fieldsByWallet[wallet][sectionId] = {}
              if (!originFieldsByWallet[wallet][sectionId]) originFieldsByWallet[wallet][sectionId] = {}

              if (!fieldsByWallet[wallet][sectionId][scope]) fieldsByWallet[wallet][sectionId][scope] = {}
              if (!originFieldsByWallet[wallet][sectionId][scope]) originFieldsByWallet[wallet][sectionId][scope] = {}

              fieldsByWallet[wallet][sectionId][scope][fieldId].sortOrder = f.sortOrder
              originFieldsByWallet[wallet][sectionId][scope][fieldId].sortOrder = f.sortOrder
            })
          } else {
            antMessage.error(`Failed to batch delete fields: ${message}`)
          }
        }

        if (batchWriteFieldsData.length) {
          const { data, message } = await batchWriteFields(batchWriteFieldsData)
          console.log('onPublish batchWriteFieldsData res', { data, message })
          if (data) {
            antMessage.success('Batch write fields successfully')
            data.forEach((f) => {
              const {
                docId, sectionId, scope, fieldId, wallet,
              } = f
              if (sectionId === 'ReleaseVer') {
                if (!releaseVerFieldsByDocId[docId]) releaseVerFieldsByDocId[docId] = {}
                if (!releaseVerFieldsByDocId[docId][scope]) releaseVerFieldsByDocId[docId][scope] = {}

                releaseVerFieldsByDocId[docId][scope][fieldId] = f
              } else {
                fieldsByWallet[wallet][sectionId][scope][fieldId] = f

                if (!originFieldsByWallet[wallet][sectionId]) originFieldsByWallet[wallet][sectionId] = {}
                if (!originFieldsByWallet[wallet][sectionId][scope]) originFieldsByWallet[wallet][sectionId][scope] = {}
                originFieldsByWallet[wallet][sectionId][scope][fieldId] = cloneDeep(f)

                delete draftSectionsByWallet[wallet]?.updatedDataBySectionId?.[sectionId]
              }
            })
          } else {
            antMessage.error(`Failed to batch write fields: ${message}`)
          }
        }

        if (batchWriteSectionsData.length) {
          spinVisible.value = true
          const { data, message } = await batchWriteSections(batchWriteSectionsData)
          spinVisible.value = false
          console.log('onPublish batchWriteSectionsData res', { data, message })

          if (data) {
            antMessage.success('Batch write sections successfully')
            data.forEach(({ sectionId, wallet }) => {
              delete draftSectionsByWallet[wallet]?.updatedDataBySectionId?.[sectionId]
            })

            const { sectionsToUpdate = [] } = data
            if (sectionsToUpdate.length) {
              sectionsToUpdate.forEach(({ sectionId, wallet, sortOrder }) => {
                sectionsByWallet[wallet][sectionId].sortOrder = sortOrder
                originSectionsByWallet[wallet][sectionId].sortOrder = sortOrder
              })
            }
          } else {
            antMessage.error(`Failed to batch write sections: ${message}`)
          }
        }

        for (const wallet of publishAffectedWallets) {
          if (!draftSectionsByWallet[wallet]?.docId) continue
          await handleWriteDraftSection(wallet)
        }

        const releaseVerPublishEntries = collectReleaseVerPublishEntries(originReleaseVerRowsByDocId)
        const snapshotHasMaster = Object.keys(publishedSnapshot).some(isMasterPublishedDocId)
        // 母本發佈不另建各子本 Release Version log；子本自己改版本才記
        if (!snapshotHasMaster) {
          releaseVerPublishEntries.forEach((entry) => {
            mergeReleaseVerPublishSnapshotEntry(publishedSnapshot, entry.docId, entry)
          })
        }

        await recordPublishActivityLog({
          publishedDocsDataByDocId: publishedSnapshot,
          updatedDataBySectionId,
          causedBy,
        })
      } finally {
        delete axios.defaults.headers.common['X-Skip-Activity-Log']
      }
    }

    const handleUpdateRemark = async () => {
      const { docId, sectionId, wallet } = selectedSectionState
      console.log('handleUpdateRemark', { docId, sectionId, remark: remarkModalState.remark })
      if (selectedSectionState.docTitle) {
        const { data, message } = await updateDoc({
          docId,
          remark: remarkModalState.remark,
        })
        if (data) {
          selectedSectionState.remark = remarkModalState.remark
          Object.assign(docsDataByDocId[data.docId], data)
          Object.assign(originalDocsDataByDocId.value[data.docId], data)
          deriveDocDataWithTitlesAndTemplateData(docsDataByDocId[data.docId], templateDataByTemplateId.value)

          // 更新 draftSection 中 updatedDoc[docId] 的 remark
          if (draftSection.value.updatedDoc?.[docId]) {
            draftSection.value.updatedDoc[docId].remark = remarkModalState.remark
          }

          remarkModalState.visible = false
          antMessage.success(`Update [${getDocShortTitle(data, templateDataByTemplateId.value, false)}] remark successfully`)
        } else {
          antMessage.error({
            duration: 10,
            content: `Failed to update doc [${selectedSectionState.docShortTitleWithoutRemark}] remark: ${message}`,
          })
        }
      } else {
        const { data, message } = await updateSection({
          docId,
          sectionId,
          remark: remarkModalState.remark,
        })

        if (data) {
          selectedSectionState.remark = remarkModalState.remark
          sectionsByWallet[wallet][sectionId] = data
          originSectionsByWallet[wallet][sectionId] = data

          // 更新 draftSection 中 updatedDataBySectionId[sectionId] 的 remark
          if (draftSection.value.updatedDataBySectionId[sectionId]?.sectionData) {
            draftSection.value.updatedDataBySectionId[sectionId].sectionData.remark = remarkModalState.remark
          }

          remarkModalState.visible = false
          antMessage.success('Update section\'s remark successfully')
        } else {
          antMessage.error({
            duration: 10,
            content: `Failed to update section's remark: ${message}`,
          })
        }
      }
    }

    /** IP 白名單與 remark 相同：直接 updateDoc，不走草稿／發布 */
    const handleUpdateAllowedIPs = async (allowedIPs) => {
      const { docId } = selectedSectionState
      if (!docId || !selectedSectionState.docTitle) return false

      const { data, message } = await updateDoc({ docId, allowedIPs })
      if (data) {
        const nextAllowedIPs = Object.prototype.hasOwnProperty.call(data, 'allowedIPs')
          ? data.allowedIPs
          : allowedIPs
        selectedSectionState.allowedIPs = nextAllowedIPs
        if (docsDataByDocId[docId]) {
          docsDataByDocId[docId].allowedIPs = nextAllowedIPs
          deriveDocDataWithTitlesAndTemplateData(docsDataByDocId[docId], templateDataByTemplateId.value)
        }
        if (originalDocsDataByDocId.value[docId]) {
          originalDocsDataByDocId.value[docId].allowedIPs = nextAllowedIPs
        }
        if (draftSection.value.updatedDoc?.[docId]) {
          draftSection.value.updatedDoc[docId].allowedIPs = nextAllowedIPs
        }
        antMessage.success(`Update [${getDocShortTitle(data, templateDataByTemplateId.value, false)}] IP White List successfully`)
        return true
      }

      antMessage.error({
        duration: 10,
        content: `Failed to update doc [${selectedSectionState.docShortTitleWithoutRemark}] IP White List: ${message}`,
      })
      return false
    }

    /** 模擬從 field 集合移除 platform（純函式，不修改 live state） */
    const simulateRemovePlatformFromFields = (fieldsBySection, platformId) => {
      if (!fieldsBySection) return { changedFields: [], hasChanges: false }

      const changedFields = []
      let hasChanges = false

      Object.values(fieldsBySection).forEach((scopeFields) => {
        Object.values(scopeFields || {}).forEach((field) => {
          const nextField = cloneDeep(field)
          let fieldChanged = false

          if (Array.isArray(nextField.platforms)) {
            const filtered = nextField.platforms.filter(e => e !== platformId)
            if (filtered.length !== nextField.platforms.length) {
              nextField.platforms = filtered
              fieldChanged = true
            }
          }

          if (nextField.scope === 'row') {
            console.log('simulateRemovePlatformFromFields nextField', nextField.platform)
            if (nextField.platform === platformId) {
              nextField.deleted = true
              fieldChanged = true
            }
          }

          if (fieldChanged) {
            hasChanges = true
            changedFields.push(nextField)
          }
        })
      })

      console.log('simulateRemovePlatformFromFields', { platformId, changedFields, hasChanges })

      return { changedFields, hasChanges }
    }

    /** 模擬移除 platform 後的 section（含 tableData / examples；fields 另由 simulateRemovePlatformFromFields 處理） */
    const simulateRemovePlatform = (section, platformId, updatedDocEntry = null) => {
      const updatedSection = cloneDeep(section)
      let hasChanges = false

      if (Array.isArray(updatedSection.platforms)) {
        const { platforms: docPlatforms = [] } = updatedDocEntry || {}
        const filtered = updatedSection.platforms.filter(e => e !== platformId)

        if (filtered.length !== updatedSection.platforms.length) {
          updatedSection.platforms = filtered
          if (docPlatforms.length && filtered.every(e => !docPlatforms.includes(e))) {
            updatedSection.deleted = true
          }
          hasChanges = true
        }
      }

      if (updatedSection.restricted?.platforms) {
        const filtered = updatedSection.restricted.platforms.filter(e => e !== platformId)
        if (filtered.length !== updatedSection.restricted.platforms.length) {
          updatedSection.restricted.platforms = filtered
          hasChanges = true
        }
      }

      if (updatedSection.tablePlatformsOrder) {
        const filtered = updatedSection.tablePlatformsOrder.filter(e => e !== platformId)
        if (filtered.length !== updatedSection.tablePlatformsOrder.length) {
          updatedSection.tablePlatformsOrder = filtered
          hasChanges = true
        }
      }

      if (updatedSection.examples) {
        Object.values(updatedSection.examples).forEach((example) => {
          if (!Array.isArray(example.platforms)) return
          const filtered = example.platforms.filter(e => e !== platformId)
          if (filtered.length !== example.platforms.length) {
            example.platforms = filtered
            hasChanges = true
          }
        })
      }

      if (!hasChanges) return null
      return updatedSection
    }

    const onBeforeDeletePlatform = async ({ id, record }) => {
      console.log('onBeforeDeletePlatform', { id, record })

      try {
        const updatedDocByDocId = {}
        for (const [docId, docData] of Object.entries(docsDataByDocId)) {
          const { platforms: docPlatforms, excludedPlatforms: docExcludedPlatforms } = docData
          if (docPlatforms?.includes(id) || docExcludedPlatforms?.includes(id)) {
            const filteredExcludedPlatforms = docExcludedPlatforms?.filter(e => e !== id)
            const filteredPlatforms = docPlatforms?.filter(e => e !== id)

            updatedDocByDocId[docId] = {
              ...docData,
              ...({ platforms: filteredPlatforms?.length ? filteredPlatforms : null }),
              ...(docExcludedPlatforms ? {
                excludedPlatforms: filteredExcludedPlatforms?.length ? filteredExcludedPlatforms : null,
              } : {}),
            }
          }
        }

        const editorUsername = localStorage.getItem('editor-username')
        const updatedTime = moment().format('YYYY/MM/DD HH:mm')
        const draftSectionByWallet = {}
        let hasAnyChange = Object.keys(updatedDocByDocId).length > 0

        for (const wallet of WALLET_KEYS) {
          const sectionsMap = sectionsByWallet[wallet]
          if (!sectionsMap) continue

          const walletUpdatedDataBySectionId = {}

          for (const section of Object.values(sectionsMap)) {
            const { sectionId } = section
            if (sectionId === 'draft' || sectionId === 'ReleaseVer') continue

            const updatedDocEntry = updatedDocByDocId[section.docId] || null
            const simulatedSection = simulateRemovePlatform(section, id, updatedDocEntry)
            const { changedFields, hasChanges: fieldsChanged } = simulateRemovePlatformFromFields(
              fieldsByWallet[wallet]?.[sectionId],
              id,
            )

            if (!simulatedSection && !fieldsChanged) continue
            hasAnyChange = true

            walletUpdatedDataBySectionId[sectionId] = {
              editorUsername,
              updatedTime,
              ...(simulatedSection ? { sectionData: simulatedSection } : {}),
              ...(changedFields.length ? { fieldsData: changedFields } : {}),
            }
          }

          const walletUpdatedDoc = Object.fromEntries(
            Object.entries(updatedDocByDocId).filter(([docId]) => getDocWallet(docId) === wallet),
          )

          if (Object.keys(walletUpdatedDataBySectionId).length || Object.keys(walletUpdatedDoc).length) {
            hasAnyChange = true
            const masterDocId = Object.keys(docsDataByDocId).find(
              docId => docId.includes('master') && getDocWallet(docId) === wallet,
            )
            draftSectionByWallet[wallet] = {
              sectionId: `staged-delete-platform-${id}`,
              wallet,
              docId: masterDocId,
              updatedDataBySectionId: walletUpdatedDataBySectionId,
              updatedDoc: walletUpdatedDoc,
            }
          }
        }

        if (!hasAnyChange) {
          if (platformClientManagementRef.value) {
            await platformClientManagementRef.value.confirmDelete({ entityType: 'platform', id, record })
          }
          return
        }

        Object.assign(stagedSection, {
          sectionId: `staged-delete-platform-${id}`,
          docId: 'master',
          draftSectionByWallet,
          deleteInfo: {
            entityType: 'platform',
            id,
            record,
          },
        })
        console.log('onBeforeDeletePlatform stagedSection', stagedSection)
        await setDraftModalType('publish')
      } catch (error) {
        console.error('onBeforeDeletePlatform error', error)
        antMessage.error({
          content: error.message,
          duration: 10,
        })
      }
    }

    const handleChangeSectionsDataByDoc = (docId, val) => {
      console.log('handleChangeSectionsDataByDoc', { docId, val })
      if (val) {
        sectionsDataByDocId[docId] = val
      } else {
        delete sectionsDataByDocId[docId]
      }
    }

    const handleChangeDocsDataByDocId = async (docId, val) => {
      console.log('handleChangeDocsDataByDocId', { docId, val })
      if (val) {
        docsDataByDocId[docId] = val
      } else {
        delete docsDataByDocId[docId]
      }
    }

    const draftModalProps = computed(() => {
      if (draftModalType.value === 'publish') {
        const isDeleteStaged = stagedSection.sectionId?.startsWith('staged-delete-')

        return {
          okText: 'Publish',
          cancelText: 'Cancel',
          okButtonProps: {
            disabled: !Object.values(draftChangeRef.value?.publishedDocsDataByDocId || []).every(({ confirmed }) => confirmed),
          },
          onOk: async () => {
            if (isDeleteStaged) {
              const { draftSectionByWallet = {}, deleteInfo } = stagedSection
              if (!deleteInfo || !platformClientManagementRef.value) return

              // 將 staged 模擬資料套用到 live state 及 draftSectionsByWallet，再寫入 DB
              for (const [wallet, walletDraft] of Object.entries(draftSectionByWallet)) {
                Object.entries(walletDraft.updatedDataBySectionId || {}).forEach(([sectionId, { sectionData, fieldsData }]) => {
                  if (sectionData) sectionsByWallet[wallet][sectionId] = cloneDeep(sectionData)
                  if (fieldsData) {
                    fieldsData.forEach((f) => {
                      if (!fieldsByWallet[wallet][sectionId]) fieldsByWallet[wallet][sectionId] = {}
                      if (!fieldsByWallet[wallet][sectionId][f.scope]) fieldsByWallet[wallet][sectionId][f.scope] = {}
                      fieldsByWallet[wallet][sectionId][f.scope][f.fieldId] = cloneDeep(f)
                    })
                  }
                })

                const draft = ensureWalletDraftSection(wallet)
                if (!draft.updatedDataBySectionId) draft.updatedDataBySectionId = {}
                Object.assign(draft.updatedDataBySectionId, walletDraft.updatedDataBySectionId || {})
                draft.updatedDoc = {
                  ...normalizeUpdatedDocByDocId(draft.updatedDoc),
                  ...normalizeUpdatedDocByDocId(walletDraft.updatedDoc),
                }
                await handleWriteDraftSection(wallet)
              }

              await onPublish({
                causedBy: {
                  type: ACTIVITY_ACTIONS.PLATFORM_DELETE,
                  entityType: 'platform',
                  entityId: deleteInfo.id,
                  entityName: entirePlatformsMap.value[deleteInfo.id]
                    || deleteInfo.record?.platformName
                    || deleteInfo.id,
                },
              })

              const success = await platformClientManagementRef.value.confirmDelete(deleteInfo)
              console.log('confirmDelete success?', success)
              if (success) {
                emptyObject(stagedSection)
                await setDraftModalType(false)
              }
            } else {
              await onPublish()
              await setDraftModalType(false)
            }
          },
          onCancel: async () => {
            await setDraftModalType(false)
            if (isDeleteStaged) emptyObject(stagedSection)
          },
        }
      }

      if (draftModalType.value === 'draft') {
        return {
          closable: false,
          maskClosable: false,
          okText: '使用',
          cancelText: '捨棄',
          onOk: () => onUseDraft(true),
          onCancel: onDiscardDraft,
        }
      }

      return {}
    })

    const apiDocWinRef = ref()

    const handleDownload = () => {
      apiDocWinRef.value = window.open(
        `/api-doc/${selectedSectionState.wallet}-wallet/${docsDataByDocId[selectedSectionState.docId].route}`,
        'winname',
        'titlebar=no,toolbar=no,location=no,width=900,height=650,top=200,left=200',
      )
    }

    const handleCopyDocLink = () => {
      if (window.navigator?.clipboard) {
        window.navigator.clipboard.writeText(docLink.value)
        antMessage.success('API Doc link is copied to clipboard successfully!')
      }
    }

    const handleMessage = ({ data: { isRedocLoaded } }) => {
      if (isRedocLoaded) {
        setTimeout(() => {
          // 檢查視窗是否仍然存在
          if (!apiDocWinRef.value || apiDocWinRef.value.closed) {
            console.warn('API Doc window is no longer available')
            return
          }

          try {
          // Auto click "Shell - Curl" language
            const apiDocWindow = apiDocWinRef.value
            const langTabs = apiDocWindow.document.querySelectorAll('li.react-tabs__tab')
            Array.prototype.filter.call(langTabs, el => RegExp('Shell - Curl').test(el.textContent)).forEach(el => el.click())

            apiDocWindow.focus()
            apiDocWindow.print()
            apiDocWindow.close()
          } catch (error) {
            console.error('Error handling API Doc window:', error)
          }
        }, 500)
      }
    }

    const EDITING_STATUS_POLL_MS = 20000
    const EDITING_STATUS_HEARTBEAT_MS = 5 * 60 * 1000
    // 超過此時間未更新 lastHeartbeatTime，視為佔權者已離線（需 > HEARTBEAT）
    const EDITING_STATUS_EXPIRE_MS = 12 * 60 * 60 * 1000
    let editingStatusPollTimer = null
    let editingStatusHeartbeatTimer = null
    let editingStatusUnloadRegistered = false

    const stopEditingStatusPoll = () => {
      if (!editingStatusPollTimer) return
      clearInterval(editingStatusPollTimer)
      editingStatusPollTimer = null
    }

    const stopEditingStatusHeartbeat = () => {
      if (!editingStatusHeartbeatTimer) return
      clearInterval(editingStatusHeartbeatTimer)
      editingStatusHeartbeatTimer = null
    }

    const formatEditingStatusTime = () => moment().format('YYYY-MM-DD HH:mm:ss')

    const isEditingStatusExpired = (editingStatusData = {}) => {
      // 舊資料可能沒有 lastHeartbeatTime，退回 editTime
      const heartbeatTime = editingStatusData.lastHeartbeatTime || editingStatusData.editTime
      if (!heartbeatTime) return true
      const lastBeat = moment(heartbeatTime, 'YYYY-MM-DD HH:mm:ss', true)
      if (!lastBeat.isValid()) return true
      return moment().diff(lastBeat) > EDITING_STATUS_EXPIRE_MS
    }

    const updateEditingStatusToFalse = () => {
      stopEditingStatusHeartbeat()

      const entityType = 'editor'
      const entityId = 'editingStatus'
      const url = `/api/api-doc/v3/management/${entityType}/${entityId}`

      const data = JSON.stringify({
        entityType,
        id: entityId,
        username: null,
        isEditing: false,
        editTime: null,
        lastHeartbeatTime: null,
      })

      if (navigator.sendBeacon) {
        const blob = new Blob([data], { type: 'application/json' })
        navigator.sendBeacon(url, blob)
      } else {
        fetch(url, {
          method: 'POST',
          body: data,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        })
      }
    }

    const registerEditingStatusUnload = () => {
      if (editingStatusUnloadRegistered) return
      editingStatusUnloadRegistered = true
      window.addEventListener('beforeunload', updateEditingStatusToFalse)
    }

    const syncEditingStatusConflict = (editingStatusData = {}) => {
      if (!editingStatusData.isEditing) return false
      if (isEditingStatusExpired(editingStatusData)) return false

      const editorUsername = localStorage.getItem('editor-username')
      if (editorUsername && editorUsername === editingStatusData.username) return false

      stopEditingStatusHeartbeat()
      editingStatusState.isEditing = true
      editingStatusState.editTime = editingStatusData.editTime
      editingStatusState.username = editingStatusData.username
      editingStatusState.modalVisible = true
      return true
    }

    // 開始佔權：寫入開始編輯時間 + 初始 heartbeat
    const updateEditingStatusToTrue = async (username) => {
      const now = formatEditingStatusTime()
      const payload = {
        entityType: 'editor',
        id: 'editingStatus',
        username,
        isEditing: true,
        editTime: now,
        lastHeartbeatTime: now,
      }

      const result = await updateManagementItem(payload)
      if (result?.message?.includes('does not exist')) {
        await createManagementItem(payload)
      }
    }

    // 僅刷新 heartbeat，不改 editTime（開始編輯時間）
    const updateEditingStatusHeartbeat = async (username) => {
      const payload = {
        entityType: 'editor',
        id: 'editingStatus',
        username,
        isEditing: true,
        lastHeartbeatTime: formatEditingStatusTime(),
      }

      const result = await updateManagementItem(payload)
      if (result?.message?.includes('does not exist')) {
        await createManagementItem({
          ...payload,
          editTime: formatEditingStatusTime(),
        })
      }
    }

    let pollEditingStatus
    let claimEditingStatus
    let startEditingStatusPoll

    const sendEditingStatusHeartbeat = async () => {
      const username = localStorage.getItem('editor-username')
      if (!username) {
        stopEditingStatusHeartbeat()
        return
      }

      try {
        const { data: editingStatusData = {} } = await getManagementByEntityTypeAndId('editor', 'editingStatus')
        if (
          editingStatusData.isEditing
          && editingStatusData.username
          && editingStatusData.username !== username
          && !isEditingStatusExpired(editingStatusData)
        ) {
          // 鎖已被他人有效佔用
          stopEditingStatusHeartbeat()
          if (syncEditingStatusConflict(editingStatusData)) {
            startEditingStatusPoll()
          }
          return
        }

        await updateEditingStatusHeartbeat(username)
      } catch (error) {
        console.error('sendEditingStatusHeartbeat failed', error)
      }
    }

    const startEditingStatusHeartbeat = () => {
      stopEditingStatusHeartbeat()
      stopEditingStatusPoll()
      editingStatusHeartbeatTimer = setInterval(
        () => sendEditingStatusHeartbeat(),
        EDITING_STATUS_HEARTBEAT_MS,
      )
    }

    startEditingStatusPoll = () => {
      stopEditingStatusPoll()
      stopEditingStatusHeartbeat()
      editingStatusPollTimer = setInterval(() => pollEditingStatus(), EDITING_STATUS_POLL_MS)
    }

    const tryClaimAfterLockReleased = async (editorUsername) => {
      editingStatusState.isEditing = false
      editingStatusState.editTime = null
      if (editorUsername) {
        editingStatusState.username = editorUsername
        editingStatusState.modalVisible = false
        const claimed = await claimEditingStatus(editorUsername)
        if (!claimed) return
      } else {
        editingStatusState.username = ''
        editingStatusState.modalVisible = true
      }
      stopEditingStatusPoll()
    }

    claimEditingStatus = async (username) => {
      const trimmedUsername = (username || '').trim()
      if (!trimmedUsername) return false

      const { data: editingStatusData = {} } = await getManagementByEntityTypeAndId('editor', 'editingStatus')
      if (
        editingStatusData.isEditing
        && editingStatusData.username !== trimmedUsername
        && !isEditingStatusExpired(editingStatusData)
      ) {
        if (syncEditingStatusConflict(editingStatusData)) {
          startEditingStatusPoll()
        }
        return false
      }

      await updateEditingStatusToTrue(trimmedUsername)
      registerEditingStatusUnload()
      startEditingStatusHeartbeat()
      return true
    }

    pollEditingStatus = async () => {
      const { data: editingStatusData = {} } = await getManagementByEntityTypeAndId('editor', 'editingStatus')
      const editorUsername = localStorage.getItem('editor-username')

      const lockReleased = !editingStatusData.isEditing
        || isEditingStatusExpired(editingStatusData)

      if (lockReleased) {
        if (editingStatusState.isEditing && editingStatusState.modalVisible) {
          await tryClaimAfterLockReleased(editorUsername)
        }
        return
      }

      syncEditingStatusConflict(editingStatusData)
    }

    const handleSetEditorUsernameToLocalStorage = async () => {
      const username = (editingStatusState.username || '').trim()
      if (!username) return

      const claimed = await claimEditingStatus(username)
      if (!claimed) return

      localStorage.setItem('editor-username', username)
      editingStatusState.modalVisible = false
      editingStatusState.isEditing = false
    }

    const editingStatusInitialized = ref(false)

    const initEditingStatus = async () => {
      // 避免重複執行
      if (editingStatusInitialized.value) return

      console.log('initEditingStatus nodeEnv', nodeEnv.value)
      if (nodeEnv.value !== 'production') return

      editingStatusInitialized.value = true

      const { data: editingStatusData = {} } = await getManagementByEntityTypeAndId('editor', 'editingStatus')
      console.log('editingStatusData', editingStatusData)

      const editorUsername = localStorage.getItem('editor-username')
      console.log('localStorage editor-username', editorUsername)

      if (syncEditingStatusConflict(editingStatusData)) {
        startEditingStatusPoll()
        return
      }

      editingStatusState.isEditing = false
      if (!editorUsername) {
        editingStatusState.modalVisible = true
        return
      }

      const claimed = await claimEditingStatus(editorUsername)
      if (!claimed) return

      editingStatusState.modalVisible = false
    }

    // 監聽 nodeEnv 變化（後備方案，使用服務端注入後通常不需要）
    // 保留此 watch 作為安全措施，以防服務端注入失敗時仍能正常工作
    watch(
      () => nodeEnv.value,
      async (newVal, oldVal) => {
        // 只在 production 環境中執行，且之前不是 production
        if (!isDev.value && newVal === 'production' && oldVal !== 'production') {
          console.log('nodeEnv changed from', oldVal, 'to', newVal)
          await initEditingStatus()
        }
      },
      { immediate: false },
    )

    onMounted(async () => {
      console.log('onMounted nodeEnv', nodeEnv.value)
      // 使用服務端注入後，nodeEnv 應該在應用啟動時就已經正確設置
      // 只在 production 環境中執行
      if (!isDev.value && nodeEnv.value === 'production') {
        await initEditingStatus()
      } else if (isDev.value) {
        // 開發環境不需要執行
      } else {
        // 非開發環境但 nodeEnv 不是 production（可能是 development 或 stage）
        // 如果服務端注入失敗，watch 會作為後備方案
        console.log('nodeEnv is not production, waiting for watch to trigger if needed...')
      }

      await nextTick(() => window.addEventListener('message', handleMessage))
    })

    onUnmounted(() => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('beforeunload', updateEditingStatusToFalse)
      stopEditingStatusPoll()
      stopEditingStatusHeartbeat()

      // 確保關閉 API Doc 視窗
      if (apiDocWinRef.value && !apiDocWinRef.value.closed) {
        apiDocWinRef.value.close()
      }
      apiDocWinRef.value = null
    })

    return {
      isDev,
      navCollapsed,
      sortDocIds,
      managementData,
      managementTreeData,
      onClickManagement,
      spinVisible,
      applyDocsModalVisible,
      createDocModalVisible,
      onChangeCreateDocModalVisible,
      publishButtonDisabled,
      selectedTreeItemState,
      docsDataByDocId,
      originalDocsDataByDocId,
      templateDataByTemplateId,
      stagedSection,
      draftSection,
      draftSectionsByWallet,
      handleWriteDraftSection,
      onSelectSectionState,
      applySelectSectionState,
      sectionsDataByDocId,
      draftModalType,
      draftModalVisible,
      setDraftModalType,
      selectedSectionState,
      originSectionsByWallet,
      sectionsByWallet,
      sectionsBySectionId,
      releaseVerSectionByDocId,
      releaseVerFieldsByDocId,
      selectedFieldState,
      originFieldsByWallet,
      fieldsByWallet,
      fieldsBySectionId,
      isMasterSectionState,
      isManagement,
      isReleaseVer,
      isDataChanged,
      isSectionStateEqual,
      isFieldStateEqual,
      activeTreeTab,
      autoSelectDraftSection,
      onChangeSelectedSectionState,
      onCancel,
      onSave,
      onSaveToDraft,
      onApplyDocsModalOk,
      onPublish,
      onUseDraft,
      onDiscardDraft,
      showPublishDraftModal,
      showSaveChangesModal,
      templateOptions,
      platformOptions,
      entirePlatformOptions,
      entirePlatformsMap,
      validateInfos,
      onChangeManagementData,
      onBeforeDeletePlatform,
      masterTreeRef,
      docTreeRef,
      editDocRef,
      apiSectionRef,
      appendixSectionRef,
      releaseVerSectionRef,
      draftChangeRef,
      applyDocsRef,
      platformClientManagementRef,
      handleChangeSectionsDataByDoc,
      handleChangeDocsDataByDocId,
      draftModalProps,
      handleDownload,
      handleCopyDocLink,
      titleCase,
      getBatchWriteSectionStateByPath,
      docLink,
      editingStatusState,
      readOnly,
      handleSetEditorUsernameToLocalStorage,
      remarkModalState,
      handleUpdateRemark,
      handleUpdateAllowedIPs,
      customizedVersionSelectedDocId,
      onAddDocToSectionFromEditDoc,
      onRemoveDocFromSectionFromEditDoc,
      shouldBeRemovedDocs,
      renderDocSelectTag,
    }
  },
}
</script>

<style scoped>
.editor-col {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.nav {
  box-shadow: 2px 0px 8px 0px #eee;
}

.nav :deep(.ant-tabs-nav-list) {
  margin-left: 16px;
}

.management {
  padding: 16px;
}

.management-item {
  margin: 4px 0;
}

.nav-collapse-btn {
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
  width: 22px;
  height: 56px;
  border: none;
  border-radius: 0 8px 8px 0;
  background: #1890ff;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.nav-collapse-btn:hover {
  background: #40a9ff;
}

.section-form {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.header-row, .footer-row {
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 24px;
  flex-shrink: 0;
}

.header-row {
  justify-content: space-between;
  box-shadow: 10px 2px 8px 0px #eee;
  z-index: 4;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 4px;
}

.header-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
}

:deep(.header-icon-buttons .anticon) {
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.header-icon-buttons .anticon:hover) {
  background-color: #f5f5f5;
  border-radius: 100%;
  padding: 4px;
  color: #1890ff;
}

.header-icon-buttons a {
  line-height: 1;
  height: auto;
}

.footer-row {
  justify-content: flex-end;
  box-shadow: 0px -2px 8px 0px #eee;
  z-index: 4;
}

.header-row h3 {
  margin: 0;
}

.draft-text {
  color: #f78789;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0.5px;
  margin-left: 8px;
}

.discard-draft-icon {
  margin-left: 2px;
  font-size: 14px;
  cursor: pointer;
  vertical-align: -0.125em;
  transition: all 0.3s;
}

.discard-draft-icon:hover {
  color: #ff4d4f;
}

.section-container {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.section-container-body {
  height: 100%;
  overflow: auto;
  padding: 32px;
}

.section-container-body--fill {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-container-body--fill :deep(.activity-log) {
  flex: 1;
  min-height: 0;
}

/* 他人編輯中：子元素不接收點擊，捲動容器本身仍可滾動 */
.section-container-body--blocked * {
  pointer-events: none;
  user-select: none;
}

.editing-status-overlay {
  position: absolute;
  inset: 0;
  z-index: 999;
  background: rgba(0, 0, 0, 0.45);
  pointer-events: none;
}

.section-container h3 {
  margin-bottom: 0;
}

.edit-remark-icon {
  color: #1890ff;
  cursor: pointer;
  margin-left: 6px;
}

.remark {
  display: flex;
  align-items: center;
  padding: 0 2px;
  margin-left: 4px;
}

.remark-text {
  color: #888;
  transition: all 0.2s ease;
  border-bottom: 1px solid transparent;
}

.remark:has(.edit-remark-icon:hover) .remark-text {
  border-bottom: 1px solid #1890ff;
}

.add-remark {
  display: flex;
  align-items: center;
  gap: 4px;color: #1890ff;
  cursor: pointer;
}

.add-customized-version-icon, .delete-customized-version-icon:hover {
  color: #1890ff;;
}
</style>

<style>
div.section-container-body.section-container-body--fill {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-container-body.section-container-body--fill .activity-log {
  flex: 1;
  min-height: 0;
}

.section-container .ant-table-tbody > tr.ant-table-row:hover > td, .ant-table-tbody > tr > td.ant-table-cell-row-hover {
  background: inherit;
}

.flex-col {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: auto;
}

.flex-col .ant-tabs-content-holder, .flex-col .ant-tabs-content, .flex-col .ant-tabs-tabpane {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: auto;
}

.ant-select-dropdown {
  width: fit-content!important;
}

.doc-warning-popover {
  z-index: 999;
}

.doc-warning-popover .ant-popover-inner {
  background-color: #fff7e6;
}

.doc-warning-popover .ant-popover-inner-content {
  padding: 8px 10px;
}

.doc-warning-popover .ant-popover-arrow-content {
  --antd-arrow-background-color: #fff7e6;
}

.prefix-input, .prefix-input:focus, .prefix-input:hover {
  color: #999999;
  background-color: #fafafa;
  cursor: default;
  border: 1px solid #d9d9d9;
  box-shadow: none;
  height: 32px;
  margin-right: 0!important;
}

</style>
